// app/lib/firebase-storage.ts
// Firebase Firestore storage functions to replace localStorage

import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  getCountFromServer,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  Timestamp,
  writeBatch,
  increment,
  deleteField,
  QueryDocumentSnapshot,
  DocumentData,
} from "firebase/firestore";
import { db, storage, app } from "./firebase";
import { auth } from "./firebase";
import { Listing, ListingOffer, Profile, PublicProfile, Report, Review, AuditLog, ConnectionStatus } from "./types";
import { calculateAverageRating } from "./rating-utils";
import { haveCompletedTransaction } from "./review-rules";

// Helper to get current user ID
function getCurrentUserId(): string | null {
  if (typeof window === "undefined") return null;
  return auth.currentUser?.uid || null;
}

// Compatibility helper: some legacy data may store sellerId as email/displayName.
function getCurrentUserIdentifiers(): string[] {
  if (typeof window === "undefined") return [];
  const user = auth.currentUser;
  if (!user) return [];

  const candidates = [user.uid, user.email || "", user.displayName || ""]
    .map((value) => String(value || "").trim())
    .filter((value) => value.length > 0);

  return Array.from(new Set(candidates));
}

// Cache for globally blocked users (admin blocks)
let globallyBlockedUsersCache: Set<string> | null = null;
let globallyBlockedUsersCacheTime = 0;
const GLOBALLY_BLOCKED_CACHE_DURATION = 60000; // 1 minute cache

// Get globally blocked users with caching (internal helper)
async function getCachedGloballyBlockedUsers(): Promise<Set<string>> {
  const now = Date.now();
  
  // Return cached data if still valid
  if (globallyBlockedUsersCache && (now - globallyBlockedUsersCacheTime) < GLOBALLY_BLOCKED_CACHE_DURATION) {
    return globallyBlockedUsersCache;
  }
  
  try {
    const blockedUsersRef = collection(db, "blockedUsers");
    const blockedSnapshot = await getDocs(blockedUsersRef);
    globallyBlockedUsersCache = new Set(blockedSnapshot.docs.map(doc => doc.id));
    globallyBlockedUsersCacheTime = now;
    return globallyBlockedUsersCache;
  } catch (error) {
    console.warn("Error fetching globally blocked users:", error);
    // Return empty set on error, or previous cache if available
    return globallyBlockedUsersCache || new Set();
  }
}

// Invalidate globally blocked users cache (call when admin blocks/unblocks a user)
export function invalidateGloballyBlockedUsersCache(): void {
  globallyBlockedUsersCache = null;
  globallyBlockedUsersCacheTime = 0;
}

// Convert Firestore timestamp to number
function timestampToNumber(ts: any): number {
  if (ts?.toMillis) return ts.toMillis();
  if (typeof ts === "number") return ts;
  return Date.now();
}

// Convert number to Firestore timestamp
function numberToTimestamp(num: number): Timestamp {
  return Timestamp.fromMillis(num);
}

// ---------- LISTINGS ----------
export async function getListings(): Promise<Listing[]> {
  try {
    const listingsRef = collection(db, "listings");
    const snapshot = await getDocs(listingsRef);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: timestampToNumber(doc.data().createdAt),
    })) as Listing[];
  } catch (error) {
    console.error("Error fetching listings:", error);
    return [];
  }
}

// Paginated listings with filters and sorting
export type ListingsQueryParams = {
  limit?: number;
  lastDoc?: QueryDocumentSnapshot<DocumentData> | null;
  category?: string;
  productType?: string;
  brand?: string;
  size?: string;
  color?: string;
  condition?: string;
  city?: string;
  minPrice?: number;
  maxPrice?: number;
  sortBy?: "newest" | "oldest" | "price-low" | "price-high";
  searchQuery?: string; // Text search (limited - Firestore doesn't support full-text search)
  // Extra filters
  onlyTrade?: boolean;   // Show only listings that allow trade/exchange
  onlyOffers?: boolean;  // Show only listings that allow offers/negotiation
  excludeSellerId?: string; // Exclude listings from this seller ID
};

export type ListingsQueryResult = {
  listings: Listing[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
};

// Client-side filter: primijeni sve filtere na listu oglasa (za fallback kad Firestore nema indeks)
function applyFiltersClientSide(
  listings: Listing[],
  params: {
    category?: string;
    productType?: string;
    brand?: string;
    size?: string;
    color?: string;
    condition?: string;
    city?: string;
    minPrice?: number;
    maxPrice?: number;
    searchQuery?: string;
    onlyTrade?: boolean;
    onlyOffers?: boolean;
    excludeSellerId?: string;
  },
  blockedUserIds: string[],
  globallyBlockedUserIds: Set<string>
): Listing[] {
  let out = listings.filter((listing) => !listing.hidden && listing.status !== "sold");
  if (params.excludeSellerId || blockedUserIds.length > 0 || globallyBlockedUserIds.size > 0) {
    out = out.filter((listing) => {
      if (params.excludeSellerId && listing.sellerId === params.excludeSellerId) return false;
      if (blockedUserIds.includes(listing.sellerId)) return false;
      if (globallyBlockedUserIds.has(listing.sellerId)) return false;
      return true;
    });
  }
  if (params.category) out = out.filter((l) => l.category === params.category);
  if (params.productType) out = out.filter((l) => l.productType === params.productType);
  if (params.brand) out = out.filter((l) => l.brand === params.brand);
  if (params.size) out = out.filter((l) => l.size === params.size);
  if (params.color) out = out.filter((l) => l.color === params.color);
  if (params.condition) out = out.filter((l) => l.condition === params.condition);
  if (params.city) out = out.filter((l) => l.city === params.city);
  if (params.minPrice != null) out = out.filter((l) => l.price >= params.minPrice!);
  if (params.maxPrice != null) out = out.filter((l) => l.price <= params.maxPrice!);
  if (params.onlyTrade) out = out.filter((l) => l.allowsTrade === true);
  if (params.onlyOffers) out = out.filter((l) => l.allowsOffers === true);
  if (params.searchQuery && params.searchQuery.trim()) {
    const searchLower = params.searchQuery.trim().toLowerCase();
    out = out.filter((listing) => {
      const searchText = (
        listing.title +
        " " +
        listing.brand +
        " " +
        listing.productType +
        " " +
        (listing.description || "") +
        " " +
        (listing.city || "") +
        " " +
        (listing.color || "") +
        " " +
        (listing.size || "") +
        " " +
        (listing.username || "")
      ).toLowerCase();
      return searchText.includes(searchLower);
    });
  }
  return out;
}

export async function getListingsPaginated(
  params: ListingsQueryParams = {}
): Promise<ListingsQueryResult> {
  const {
    limit: pageLimit = 20,
    lastDoc,
    category,
    productType,
    brand,
    size,
    color,
    condition,
    city,
    minPrice,
    maxPrice,
    sortBy = "newest",
    searchQuery,
    onlyTrade,
    onlyOffers,
    excludeSellerId,
  } = params;

  const userId = getCurrentUserId();
  let blockedUsersPromise: Promise<string[]> | null = null;
  let globallyBlockedUsersPromise: Promise<Set<string>> | null = null;
  if (userId) {
    blockedUsersPromise = getBlockedUsers().catch(() => []);
    globallyBlockedUsersPromise = getCachedGloballyBlockedUsers().catch(() => new Set<string>());
  }

  const runQuery = async (useFallback: boolean): Promise<ListingsQueryResult> => {
    const listingsRef = collection(db, "listings");
    let q = query(listingsRef);

    if (!useFallback) {
      if (category) q = query(q, where("category", "==", category));
      if (productType) q = query(q, where("productType", "==", productType));
      if (brand) q = query(q, where("brand", "==", brand));
      if (size) q = query(q, where("size", "==", size));
      if (color) q = query(q, where("color", "==", color));
      if (condition) q = query(q, where("condition", "==", condition));
      if (city) q = query(q, where("city", "==", city));
      if (minPrice !== undefined && minPrice !== null) q = query(q, where("price", ">=", minPrice));
      if (maxPrice !== undefined && maxPrice !== null) q = query(q, where("price", "<=", maxPrice));
      if (onlyTrade) q = query(q, where("allowsTrade", "==", true));
      if (onlyOffers) q = query(q, where("allowsOffers", "==", true));
    }

    if (sortBy === "newest" || sortBy === "oldest") {
      q = query(q, orderBy("createdAt", sortBy === "newest" ? "desc" : "asc"));
    } else if (sortBy === "price-low" || sortBy === "price-high") {
      q = query(q, orderBy("price", sortBy === "price-low" ? "asc" : "desc"));
    } else {
      q = query(q, orderBy("createdAt", "desc"));
    }

    const fetchLimit = useFallback ? 200 : pageLimit;
    q = query(q, limit(fetchLimit));
    if (lastDoc) q = query(q, startAfter(lastDoc));

    const snapshot = await getDocs(q);
    const listings = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: timestampToNumber(doc.data().createdAt),
    })) as Listing[];

    let blockedUserIds: string[] = [];
    let globallyBlockedUserIds: Set<string> = new Set();
    if (blockedUsersPromise) blockedUserIds = await blockedUsersPromise;
    if (globallyBlockedUsersPromise) globallyBlockedUserIds = await globallyBlockedUsersPromise;

    const filterParams = {
      category,
      productType,
      brand,
      size,
      color,
      condition,
      city,
      minPrice,
      maxPrice,
      searchQuery,
      onlyTrade,
      onlyOffers,
      excludeSellerId,
    };

    let filteredListings = useFallback
      ? applyFiltersClientSide(listings, filterParams, blockedUserIds, globallyBlockedUserIds)
      : listings.filter((l) => !l.hidden && l.status !== "sold");

    if (!useFallback) {
      if (blockedUserIds.length > 0 || globallyBlockedUserIds.size > 0 || excludeSellerId) {
        filteredListings = filteredListings.filter((listing) => {
          if (excludeSellerId && listing.sellerId === excludeSellerId) return false;
          if (blockedUserIds.includes(listing.sellerId)) return false;
          if (globallyBlockedUserIds.has(listing.sellerId)) return false;
          return true;
        });
      }
      if (searchQuery && searchQuery.trim()) {
        const searchLower = searchQuery.trim().toLowerCase();
        filteredListings = filteredListings.filter((listing) => {
          const searchText = (
            listing.title + " " + listing.brand + " " + listing.productType +
            " " + (listing.description || "") + " " + (listing.city || "") +
            " " + (listing.color || "") + " " + (listing.size || "") + " " + (listing.username || "")
          ).toLowerCase();
          return searchText.includes(searchLower);
        });
      }
    }

    if (sortBy === "price-low" || sortBy === "price-high") {
      filteredListings = [...filteredListings].sort((a, b) =>
        sortBy === "price-low" ? a.price - b.price : b.price - a.price
      );
    }

    const toReturn = useFallback ? filteredListings.slice(0, pageLimit) : filteredListings;
    const newLastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
    const hasMore = useFallback ? snapshot.docs.length === fetchLimit : snapshot.docs.length === pageLimit;

    return { listings: toReturn, lastDoc: newLastDoc, hasMore };
  };

  try {
    return await runQuery(false);
  } catch (error: any) {
    if (error?.code === "failed-precondition") {
      try {
        return await runQuery(true);
      } catch (fallbackError: any) {
        console.error("Firestore fallback query failed:", fallbackError);
        return { listings: [], lastDoc: null, hasMore: false };
      }
    }
    console.error("Error fetching paginated listings:", error);
    return { listings: [], lastDoc: null, hasMore: false };
  }
}

export async function getListingsCount(
  params: Omit<ListingsQueryParams, 'limit' | 'lastDoc' | 'sortBy'> = {}
): Promise<number> {
  try {
    const {
      category,
      productType,
      brand,
      size,
      color,
      condition,
      city,
      minPrice,
      maxPrice,
      searchQuery,
      onlyTrade,
      onlyOffers,
      excludeSellerId,
    } = params;

    const userId = getCurrentUserId();
    
    // Get blocked users (user-to-user blocks)
    let blockedUserIds: string[] = [];
    if (userId) {
      try {
        blockedUserIds = await getBlockedUsers();
      } catch (error) {
        console.warn("Error fetching blocked users:", error);
      }
    }
    
    // Get globally blocked users (admin blocks) - use cached version
    let globallyBlockedUserIds: Set<string> = new Set();
    if (userId) {
      globallyBlockedUserIds = await getCachedGloballyBlockedUsers();
    }

    const listingsRef = collection(db, "listings");
    let q = query(listingsRef);

    // Apply filters (same as getListingsPaginated)
    if (category) {
      q = query(q, where("category", "==", category));
    }
    if (productType) {
      q = query(q, where("productType", "==", productType));
    }
    if (brand) {
      q = query(q, where("brand", "==", brand));
    }
    if (size) {
      q = query(q, where("size", "==", size));
    }
    if (color) {
      q = query(q, where("color", "==", color));
    }
    if (condition) {
      q = query(q, where("condition", "==", condition));
    }
    if (city) {
      q = query(q, where("city", "==", city));
    }
    if (minPrice !== undefined && minPrice !== null) {
      q = query(q, where("price", ">=", minPrice));
    }
    if (maxPrice !== undefined && maxPrice !== null) {
      q = query(q, where("price", "<=", maxPrice));
    }
    if (onlyTrade) {
      q = query(q, where("allowsTrade", "==", true));
    }
    if (onlyOffers) {
      q = query(q, where("allowsOffers", "==", true));
    }

    // Get all matching listings (we need to filter client-side for excludeSellerId, hidden, sold, etc.)
    const snapshot = await getDocs(q);
    const listings = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Listing[];
    
    // Filter listings
    let filtered = listings.filter((listing) => {
      if (excludeSellerId && listing.sellerId === excludeSellerId) return false;
      if (listing.hidden) return false;
      if (listing.status === "sold") return false;
      if (blockedUserIds.includes(listing.sellerId)) return false;
      if (globallyBlockedUserIds.has(listing.sellerId)) return false;
      return true;
    });

    // Apply text search if needed
    if (searchQuery && searchQuery.trim()) {
      const searchLower = searchQuery.trim().toLowerCase();
      filtered = filtered.filter((listing) => {
        const searchText = (
          listing.title +
          " " +
          listing.brand +
          " " +
          listing.productType +
          " " +
          (listing.description || "") +
          " " +
          (listing.city || "") +
          " " +
          (listing.color || "") +
          " " +
          (listing.size || "") +
          " " +
          (listing.username || "")
        ).toLowerCase();
        return searchText.includes(searchLower);
      });
    }

    return filtered.length;
  } catch (error: any) {
    console.error("Error getting listings count:", error);
    return 0;
  }
}

export function subscribeToListings(
  callback: (listings: Listing[]) => void
): () => void {
  const listingsRef = collection(db, "listings");
  const q = query(listingsRef, orderBy("createdAt", "desc"));
  
  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const listings = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: timestampToNumber(doc.data().createdAt),
      })) as Listing[];
      callback(listings);
    },
    (error) => {
      console.error("Error subscribing to listings:", error);
      callback([]);
    }
  );

  return unsubscribe;
}

export async function getMyListings(userId?: string): Promise<Listing[]> {
  try {
    const fallbackIds = getCurrentUserIdentifiers();
    const requestedId = String(userId || "").trim();
    const ownerIds = Array.from(new Set([requestedId, ...fallbackIds].filter((value) => value.length > 0)));
    if (ownerIds.length === 0) return [];

    const listingsRef = collection(db, "listings");
    const listingFromDoc = (docSnap: any): Listing =>
      ({
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: timestampToNumber(docSnap.data().createdAt),
      }) as Listing;

    const addSnapshotToMap = (snapshot: any, map: Map<string, Listing>) => {
      snapshot.docs.forEach((docSnap: any) => {
        const listing = listingFromDoc(docSnap);
        if (listing.status !== "sold") {
          map.set(String(listing.id), listing);
        }
      });
    };

    const deduped = new Map<string, Listing>();

    // Fast path: when caller provides a specific UID (standard case), query only that ID first.
    // This avoids additional fallback queries for every page load.
    if (requestedId) {
      const primarySnapshot = await getDocs(query(listingsRef, where("sellerId", "==", requestedId)));
      addSnapshotToMap(primarySnapshot, deduped);
      if (deduped.size > 0) {
        return Array.from(deduped.values()).sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0));
      }
    }

    // Fallback path: legacy records may use email/displayName as sellerId.
    const fallbackOwnerIds = ownerIds.filter((ownerId) => ownerId !== requestedId).slice(0, 9);
    if (fallbackOwnerIds.length > 0) {
      const snapshots = await Promise.all(
        fallbackOwnerIds.map((ownerId) => getDocs(query(listingsRef, where("sellerId", "==", ownerId))))
      );
      snapshots.forEach((snapshot) => addSnapshotToMap(snapshot, deduped));
    }

    return Array.from(deduped.values()).sort((a, b) => {
      const aTs = Number(a.createdAt || 0);
      const bTs = Number(b.createdAt || 0);
      return bTs - aTs;
    });
  } catch (error) {
    console.error("Error fetching my listings:", error);
    return [];
  }
}

export async function getListingById(id: string): Promise<Listing | null> {
  try {
    const docRef = doc(db, "listings", id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return {
      id: docSnap.id,
      ...docSnap.data(),
      createdAt: timestampToNumber(docSnap.data().createdAt),
    } as Listing;
  } catch (error) {
    console.error("Error fetching listing:", error);
    return null;
  }
}

// ---------- LISTING OFFERS ----------

export async function addListingOffer(
  listingId: string,
  buyerId: string,
  amount: number,
  message?: string
): Promise<string> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");
  if (!buyerId || buyerId !== userId) {
    throw new Error("buyerId must match authenticated user");
  }

  // Get listing to verify it exists and get seller info for notification
  const listingRef = doc(db, "listings", listingId);
  const listingSnap = await getDoc(listingRef);
  if (!listingSnap.exists()) {
    throw new Error("Listing not found");
  }
  const listing = listingSnap.data() as Listing;

  const offersRef = collection(db, "listings", listingId, "offers");
  const offerRef = doc(offersRef);
  const offerData: any = {
    listingId,
    buyerId,
    amount,
    createdAt: numberToTimestamp(Date.now()),
  };

  // Firestore ne dozvoljava undefined – dodaj message samo ako postoji
  if (message && message.trim().length > 0) {
    offerData.message = message.trim();
  }

  await setDoc(offerRef, offerData);

  // Create notification for the listing owner
  if (listing.sellerId && listing.sellerId !== userId) {
    try {
      const { createOfferNotification } = await import("./notifications");
      await createOfferNotification(
        listing.sellerId,
        userId,
        message || `Ponuda od ${amount} KM`,
        listingId,
        listing.title,
        amount
      );
    } catch (notifError) {
      // Don't fail offer creation if notification fails
      console.error("Error creating offer notification:", notifError);
    }
  }

  return offerRef.id;
}

export async function getListingOffers(listingId: string): Promise<ListingOffer[]> {
  const offersRef = collection(db, "listings", listingId, "offers");
  const qOffers = query(offersRef, orderBy("createdAt", "desc"));
  const snapshot = await getDocs(qOffers);
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data() as any;
    return {
      id: docSnap.id,
      listingId,
      buyerId: String(data.buyerId || ""),
      amount: Number(data.amount || 0),
      message: data.message || "",
      createdAt: typeof data.createdAt === "number" ? data.createdAt : data.createdAt?.toMillis?.() || Date.now(),
    } as ListingOffer;
  });
}

export async function addListing(item: Omit<Listing, "id">): Promise<string> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Get user profile to populate username/displayName
  let displayName = item.username || "Korisnik";
  try {
    const profile = await getProfile(userId);
    displayName = getDisplayName(profile, userId);
  } catch (error) {
    console.warn("Error fetching profile for listing, using fallback:", error);
  }

  const listingRef = doc(collection(db, "listings"));
  const listingData: any = {
    ...item,
    username: displayName, // Store displayName as username for backward compatibility
    createdAt: numberToTimestamp(item.createdAt || Date.now()),
    sellerId: userId,
  };

  // Skip migration - store base64 images directly in Firestore
  // This is faster and avoids CORS issues when Storage Rules are not configured
  if (listingData.imageUrls && Array.isArray(listingData.imageUrls)) {
    console.log(`[addListing] Saving ${listingData.imageUrls.length} images directly to Firestore (base64)...`);
    // Keep base64 images as-is, no migration needed
    console.log(`[addListing] Images ready:`, {
      count: listingData.imageUrls.length,
      firstImage: listingData.imageUrls[0]?.substring(0, 50) + '...'
    });
  } else {
    console.log(`[addListing] No imageUrls array provided or empty`);
  }

  // Ensure imageUrl is set from imageUrls if not already set
  if (!listingData.imageUrl && listingData.imageUrls && listingData.imageUrls.length > 0) {
    listingData.imageUrl = listingData.imageUrls[0];
    console.log(`[addListing] Set imageUrl from imageUrls[0]:`, listingData.imageUrl);
  }

  // Ensure imageUrls is always an array if we have images
  if (listingData.imageUrl && !listingData.imageUrls) {
    listingData.imageUrls = [listingData.imageUrl];
    console.log(`[addListing] Created imageUrls array from imageUrl:`, listingData.imageUrls);
  }

  // Remove undefined values (Firebase doesn't allow undefined)
  // But keep imageUrls even if it's an empty array (we'll handle that)
  Object.keys(listingData).forEach(key => {
    if (listingData[key] === undefined) {
      delete listingData[key];
    }
  });

  // Ensure imageUrls is always set if we have imageUrl
  if (listingData.imageUrl && (!listingData.imageUrls || listingData.imageUrls.length === 0)) {
    listingData.imageUrls = [listingData.imageUrl];
    console.log(`[addListing] Ensured imageUrls array exists:`, listingData.imageUrls);
  }

  console.log(`[addListing] Saving listing to Firestore:`, {
    imageUrl: listingData.imageUrl,
    imageUrls: listingData.imageUrls,
    imageUrlsCount: listingData.imageUrls?.length || 0,
    hasImageUrls: !!listingData.imageUrls
  });

  await setDoc(listingRef, listingData);
  console.log(`[addListing] Listing saved with ID: ${listingRef.id}`);
  return listingRef.id;
}

export async function updateListing(updated: Listing): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Verify ownership
  const existing = await getListingById(updated.id);
  if (existing && existing.sellerId !== userId) {
    throw new Error("You can only update your own listings");
  }

  const listingRef = doc(db, "listings", updated.id);
  const { id, ...data } = updated;
  const updateData: any = {
    ...data,
    createdAt: numberToTimestamp(data.createdAt || Date.now()),
  };

  // Remove undefined values (Firebase doesn't allow undefined)
  Object.keys(updateData).forEach(key => {
    if (updateData[key] === undefined) {
      delete updateData[key];
    }
  });

  await updateDoc(listingRef, updateData);
}

// Helper function to extract storage path from Firebase Storage download URL
function extractStoragePathFromUrl(url: string): string | null {
  try {
    // Firebase Storage download URL format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encodedPath}?alt=media&token={token}
    if (url.includes("firebasestorage.googleapis.com") && url.includes("/o/")) {
      const match = url.match(/\/o\/([^?]+)/);
      if (match && match[1]) {
        // Decode the path (URL encoded)
        return decodeURIComponent(match[1]);
      }
    }
    // If it's a gs:// URL, extract the path after gs://bucket/
    if (url.startsWith("gs://")) {
      const match = url.match(/gs:\/\/[^\/]+\/(.+)/);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  } catch (error) {
    console.warn("Error extracting storage path from URL:", error);
    return null;
  }
}

// Helper function to delete a file from Firebase Storage by URL
async function deleteFileFromStorageByUrl(url: string): Promise<void> {
  try {
    const storagePath = extractStoragePathFromUrl(url);
    if (!storagePath) {
      // Not a Firebase Storage URL, skip deletion
      return;
    }
    
    const { ref, deleteObject } = await import("firebase/storage");
    if (!storage) {
      console.warn("Firebase Storage not initialized");
      return;
    }
    
    const fileRef = ref(storage, storagePath);
    await deleteObject(fileRef);
  } catch (error) {
    console.warn("Error deleting file from storage:", error);
    // Continue even if deletion fails
  }
}

export async function deleteListing(id: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Verify ownership (with admin override)
  const existing = await getListingById(id);
  const currentIdentifiers = getCurrentUserIdentifiers();
  const isOwner =
    !!existing &&
    currentIdentifiers.some(
      (identifier) =>
        String(existing.sellerId || "").trim().toLowerCase() ===
        String(identifier || "").trim().toLowerCase()
    );
  if (existing && !isOwner) {
    try {
      const { isAdminSync } = await import("./auth");
      const isAdminUser = isAdminSync(userId);
      if (!isAdminUser) {
        throw new Error("You can only delete your own listings");
      }
    } catch (error) {
      // If admin check fails for any reason, fall back to strict ownership rule
      throw new Error("You can only delete your own listings");
    }
  }

  // Delete listing images from Firebase Storage if they exist
  if (existing) {
    // Delete main imageUrl if it's a Firebase Storage URL
    if (existing.imageUrl) {
      await deleteFileFromStorageByUrl(existing.imageUrl);
    }
    
    // Delete all images from imageUrls array
    if (existing.imageUrls && Array.isArray(existing.imageUrls)) {
      for (const imageUrl of existing.imageUrls) {
        if (imageUrl) {
          await deleteFileFromStorageByUrl(imageUrl);
        }
      }
    }
    
    // Delete video if it exists
    if (existing.videoUrl) {
      await deleteFileFromStorageByUrl(existing.videoUrl);
    }
  }

  // Delete all favorites for this listing (across all users)
  // Note: Firestore doesn't support collection group queries for subcollections efficiently
  // So we need to iterate through all profiles. In production, consider using a Cloud Function
  // or maintaining a reverse index (favorites/{listingId}/users/{userId})
  try {
    const profilesRef = collection(db, "profiles");
    const profilesSnapshot = await getDocs(profilesRef);
    
    let currentBatch = writeBatch(db);
    let batchCount = 0;
    const MAX_BATCH_SIZE = 500; // Firestore batch limit

    for (const profileDoc of profilesSnapshot.docs) {
      const favoriteRef = doc(db, "profiles", profileDoc.id, "favorites", id);
      currentBatch.delete(favoriteRef);
      batchCount++;

      // Firestore batch limit is 500 operations
      if (batchCount >= MAX_BATCH_SIZE) {
        await currentBatch.commit();
        // Start new batch
        currentBatch = writeBatch(db);
        batchCount = 0;
      }
    }

    if (batchCount > 0) {
      await currentBatch.commit();
    }
  } catch (error) {
    console.warn("Error deleting favorites for listing:", error);
    // Continue with listing deletion even if favorites deletion fails
  }

  // Delete all reports for this listing
  try {
    const reportsRef = collection(db, "reports");
    const reportsQuery = query(
      reportsRef,
      where("targetType", "==", "listing"),
      where("targetId", "==", id)
    );
    const reportsSnapshot = await getDocs(reportsQuery);

    const batch = writeBatch(db);
    reportsSnapshot.docs.forEach((reportDoc) => {
      batch.delete(reportDoc.ref);
    });

    if (reportsSnapshot.docs.length > 0) {
      await batch.commit();
    }
  } catch (error) {
    console.warn("Error deleting reports for listing:", error);
    // Continue with listing deletion even if reports deletion fails
  }

  // Delete all reviews for this listing (optional cleanup)
  try {
    const reviewsRef = collection(db, "reviews");
    const reviewsQuery = query(
      reviewsRef,
      where("targetType", "==", "listing"),
      where("targetId", "==", id)
    );
    const reviewsSnapshot = await getDocs(reviewsQuery);

    const batch = writeBatch(db);
    reviewsSnapshot.docs.forEach((reviewDoc) => {
      batch.delete(reviewDoc.ref);
    });

    if (reviewsSnapshot.docs.length > 0) {
      await batch.commit();
    }
  } catch (error) {
    console.warn("Error deleting reviews for listing:", error);
    // Continue with listing deletion even if reviews deletion fails
  }

  // Delete all offers for this listing (optional cleanup)
  try {
    const offersRef = collection(db, "listings", id, "offers");
    const offersSnapshot = await getDocs(offersRef);
    const batch = writeBatch(db);
    offersSnapshot.docs.forEach((offerDoc) => {
      batch.delete(offerDoc.ref);
    });
    if (offersSnapshot.docs.length > 0) {
      await batch.commit();
    }
  } catch (error) {
    console.warn("Error deleting offers for listing:", error);
    // Continue with listing deletion even if offers deletion fails
  }

  // Delete all notifications related to this listing
  try {
    const notificationsRef = collection(db, "notifications");
    
    // Delete offer notifications
    const offerNotificationsQuery = query(
      notificationsRef,
      where("type", "==", "offer")
    );
    const offerNotificationsSnapshot = await getDocs(offerNotificationsQuery);
    
    // Delete listing_closed notifications
    const closedNotificationsQuery = query(
      notificationsRef,
      where("type", "==", "listing_closed")
    );
    const closedNotificationsSnapshot = await getDocs(closedNotificationsQuery);
    
    // Delete rating_reminder notifications
    const reminderNotificationsQuery = query(
      notificationsRef,
      where("type", "==", "rating_reminder")
    );
    const reminderNotificationsSnapshot = await getDocs(reminderNotificationsQuery);
    
    const batch = writeBatch(db);
    let hasDeletions = false;
    
    // Process offer notifications
    offerNotificationsSnapshot.docs.forEach((notifDoc) => {
      const data = notifDoc.data();
      if (data.offerData && data.offerData.listingId === id) {
        batch.delete(notifDoc.ref);
        hasDeletions = true;
      }
    });
    
    // Process listing_closed notifications
    closedNotificationsSnapshot.docs.forEach((notifDoc) => {
      const data = notifDoc.data();
      if (data.listingClosedData && data.listingClosedData.listingId === id) {
        batch.delete(notifDoc.ref);
        hasDeletions = true;
      }
    });
    
    // Process rating_reminder notifications
    reminderNotificationsSnapshot.docs.forEach((notifDoc) => {
      const data = notifDoc.data();
      if (data.ratingReminderData && data.ratingReminderData.listingId === id) {
        batch.delete(notifDoc.ref);
        hasDeletions = true;
      }
    });
    
    if (hasDeletions) {
      await batch.commit();
    }
  } catch (error) {
    console.warn("Error deleting notifications for listing:", error);
    // Continue with listing deletion even if notifications deletion fails
  }

  // Finally, delete the listing itself
  try {
    const listingRef = doc(db, "listings", id);
    await deleteDoc(listingRef);
    console.log(`Listing ${id} successfully deleted`);
  } catch (error: any) {
    console.error("Error deleting listing document:", error);
    // If it's a permission error, throw it so the user knows
    if (error?.code === "permission-denied" || error?.code === "missing-or-insufficient-permissions") {
      throw new Error("Nemate dozvolu za brisanje ovog oglasa.");
    }
    // If document doesn't exist, that's fine (idempotent operation)
    if (error?.code === "not-found") {
      console.log(`Listing ${id} does not exist, deletion considered successful`);
      return;
    }
    // For other errors, rethrow
    throw error;
  }
}

// ---------- PROFILE ----------
const DEFAULT_PROFILE: Profile = {
  id: "default",
  displayName: "Korisnik",
  city: "Sarajevo",
  bio: "",
  avatarUrl: "",
  memberSince: Date.now(),
};

// Helper function to get display name from profile (with fallback)
export function getDisplayName(profile: Profile | null | undefined, userId?: string): string {
  if (!profile) {
    return userId ? `Korisnik ${userId.substring(0, 8)}...` : "Korisnik";
  }
  
  // Use displayName if available
  if (profile.displayName) {
    return profile.displayName;
  }
  
  // Fallback: construct from name + surname or use username
  if (profile.name) {
    const fullName = profile.surname ? `${profile.name} ${profile.surname}` : profile.name;
    return fullName;
  }
  
  if (profile.username) {
    return profile.username;
  }
  
  // Last resort fallback
  return userId ? `Korisnik ${userId.substring(0, 8)}...` : "Korisnik";
}

// Helper function to get avatar URL from profile (with fallback)
export function getAvatarUrl(profile: Profile | null | undefined): string {
  if (!profile) return "";
  return profile.avatarUrl || "";
}

// Get public profile data (safe to expose to all users)
export async function getPublicProfile(userId: string): Promise<PublicProfile> {
  try {
    const profile = await getProfile(userId);
    return {
      id: profile.id,
      displayName: getDisplayName(profile, userId),
      avatarUrl: profile.avatarUrl || undefined,
      city: profile.city || undefined,
      bio: profile.bio || undefined,
      memberSince: profile.memberSince || undefined,
    };
  } catch (error) {
    console.warn("Error fetching public profile, using fallback:", error);
    return {
      id: userId,
      displayName: `Korisnik ${userId.substring(0, 8)}...`,
      avatarUrl: undefined,
      city: undefined,
      bio: undefined,
      memberSince: undefined,
    };
  }
}

// Mark listing as sold and record buyer
export async function closeListing(listingId: string, buyerId: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) {
    throw new Error("User must be authenticated to close a listing");
  }

  const listingRef = doc(db, "listings", listingId);
  const snap = await getDoc(listingRef);
  if (!snap.exists()) {
    throw new Error("Listing not found");
  }

  const data = snap.data() as any;
  if (data.sellerId !== userId) {
    throw new Error("Možeš zatvoriti samo svoje oglase");
  }

  await updateDoc(listingRef, {
    status: "sold",
    buyerId,
    closedAt: numberToTimestamp(Date.now()),
  });

  // Create notification for buyer when listing is closed
  try {
    const { createListingClosedNotification } = await import("./notifications");
    await createListingClosedNotification(
      buyerId,
      userId,
      listingId,
      data.title || "Oglas"
    );
  } catch (notifError) {
    console.error("Error creating listing closed notification:", notifError);
  }

  // Note: Automatic chat message removed - notifications are sufficient
}

// Find all counterparties (buyers/sellers) that current user had at least one
// completed transaction with.
// 
// Gledamo i listings (zatvorene transakcije) i reviews (ocjene) da bismo
// pronašli sve korisnike sa kojima je korisnik imao transakciju.
export async function getUsersWithCompletedTransactions(
  forUserId?: string
): Promise<string[]> {
  const userId = forUserId || getCurrentUserId();
  if (!userId) return [];

  try {
    const counterparties = new Set<string>();

    // 1. Pronađi sve zatvorene oglase gdje je korisnik bio prodavač ili kupac
    const listingsRef = collection(db, "listings");
    const [asSellerSnap, asBuyerSnap] = await Promise.all([
      // Oglasi gdje je korisnik prodavač i ima buyerId (zatvoreno)
      getDocs(query(listingsRef, where("sellerId", "==", userId))),
      // Oglasi gdje je korisnik kupac (buyerId postavljen)
      getDocs(query(listingsRef, where("buyerId", "==", userId))),
    ]);

    // Iz listings gdje je korisnik prodavač, uzmi buyerId
    asSellerSnap.forEach((docSnap) => {
      const data = docSnap.data() as any;
      if (data?.buyerId && String(data.buyerId) !== userId) {
        counterparties.add(String(data.buyerId));
      }
    });

    // Iz listings gdje je korisnik kupac, uzmi sellerId
    asBuyerSnap.forEach((docSnap) => {
      const data = docSnap.data() as any;
      if (data?.sellerId && String(data.sellerId) !== userId) {
        counterparties.add(String(data.sellerId));
      }
    });

    // 2. Također gledaj reviews za dodatne veze (ako postoje)
    const reviewsRef = collection(db, "reviews");
    const [aboutMeSnap, byMeSnap] = await Promise.all([
      getDocs(query(reviewsRef, where("targetId", "==", userId))),
      getDocs(query(reviewsRef, where("reviewerId", "==", userId))),
    ]);

    aboutMeSnap.forEach((docSnap) => {
      const data = docSnap.data() as any;
      if (data?.targetType === "user" && data?.reviewerId && data.reviewerId !== userId) {
        counterparties.add(String(data.reviewerId));
      }
    });

    byMeSnap.forEach((docSnap) => {
      const data = docSnap.data() as any;
      if (data?.targetType === "user" && data?.targetId && data.targetId !== userId) {
        counterparties.add(String(data.targetId));
      }
    });

    const result = Array.from(counterparties);

    if (typeof window !== "undefined") {
      console.log("[getUsersWithCompletedTransactions] userId =", userId, "counterparties =", result);
    }

    return result;
  } catch (error) {
    console.error("Error loading users with completed transactions:", error);
    return [];
  }
}

export async function getProfile(userId?: string): Promise<Profile> {
  const targetUserId = userId || getCurrentUserId();
  if (!targetUserId) return DEFAULT_PROFILE;

  // Guard: don't call Firestore if user is not authenticated (for reading other profiles)
  // If reading own profile, we can still try, but for others we need auth
  const currentUserId = auth.currentUser?.uid;
  if (!currentUserId) {
    // If trying to read another user's profile without being authenticated, return default
    if (userId) {
      console.warn("[FIRESTORE] getProfile called for other user without authentication");
      return {
        ...DEFAULT_PROFILE,
        id: targetUserId,
        displayName: `Korisnik ${targetUserId.substring(0, 8)}...`,
      };
    }
    // If reading own profile but not authenticated, return default
    console.warn("[FIRESTORE] getProfile called without authenticated user");
    return DEFAULT_PROFILE;
  }

  try {
    // Ako je korisnik označen kao obrisan u deletedUsers, tretiraj nalog kao obrisan
    if (targetUserId === auth.currentUser?.uid) {
      try {
        const deletedRef = doc(db, "deletedUsers", targetUserId);
        const deletedSnap = await getDoc(deletedRef);
        if (deletedSnap.exists()) {
          console.warn("[FIRESTORE] getProfile: account is marked as deleted, signing out");
          try {
            await auth.signOut();
          } catch (signOutError: any) {
            console.warn("[FIRESTORE] Error signing out deleted account:", signOutError?.message || String(signOutError));
          }
          const error = new Error("PROFILE_DELETED");
          (error as any).code = "PROFILE_DELETED";
          throw error;
        }
      } catch (tombstoneError) {
        console.warn("[FIRESTORE] Error checking deletedUsers tombstone:", tombstoneError);
      }
    }

    const profileRef = doc(db, "profiles", targetUserId);
    const profileSnap = await getDoc(profileRef);
    
    if (!profileSnap.exists()) {
      // Ako profil ne postoji za trenutnog korisnika, tretiramo nalog kao obrisan
      if (targetUserId === auth.currentUser?.uid) {
        console.warn("[FIRESTORE] Profile missing for current user, treating as deleted account");
        try {
          await auth.signOut();
        } catch (signOutError: any) {
          console.warn("[FIRESTORE] Error signing out deleted account:", signOutError?.message || String(signOutError));
        }
        // Bacamo specifičnu grešku koju možemo prepoznati u wrapperu
        const error = new Error("PROFILE_DELETED");
        (error as any).code = "PROFILE_DELETED";
        throw error;
      }
      // Za druge korisnike samo vrati default bez kreiranja profila
      return {
        ...DEFAULT_PROFILE,
        id: targetUserId,
        displayName: `Korisnik ${targetUserId.substring(0, 8)}...`,
      };
    }

    const data = profileSnap.data();
    const profileData: Profile = {
      id: profileSnap.id,
      ...data,
      memberSince: timestampToNumber(data.memberSince),
    } as Profile;
    
    // Ensure displayName exists (construct from name/surname/username if needed)
    // Prioritize name+surname over existing displayName (especially if displayName is just "Korisnik")
    if (profileData.name) {
      if (profileData.surname) {
        profileData.displayName = `${profileData.name} ${profileData.surname}`.trim();
      } else {
        profileData.displayName = profileData.name.trim();
      }
    } else if (profileData.username) {
      profileData.displayName = profileData.username.trim();
    } else if (!profileData.displayName || profileData.displayName === "Korisnik") {
      // Only use existing displayName if it's not the default "Korisnik"
      profileData.displayName = profileData.displayName || "Korisnik";
    }
    
    return profileData;
  } catch (error: any) {
    // Only log error if it's not a permission error (which is expected for unauthenticated users)
    const errorCode = error?.code;
    const errorMessage = error?.message || String(error) || "Unknown error";
    
    // Don't log permission errors as errors - they're expected in some cases (e.g., during registration)
    if (errorCode !== "permission-denied" && errorCode !== "missing-or-insufficient-permissions") {
      // Only log if we have meaningful error information
      const errorLog: any = {
        action: "fetchProfile",
        userId: targetUserId,
        path: `profiles/${targetUserId}`,
      };
      
      let hasMeaningfulInfo = false;
      
      if (errorCode && typeof errorCode === "string" && errorCode.length > 0 && errorCode.trim().length > 0) {
        errorLog.code = errorCode;
        hasMeaningfulInfo = true;
      }
      
      if (errorMessage && 
          typeof errorMessage === "string" && 
          errorMessage !== "Unknown error" && 
          errorMessage !== "[object Object]" &&
          errorMessage !== "{}" &&
          errorMessage.trim().length > 0) {
        errorLog.error = errorMessage;
        hasMeaningfulInfo = true;
      }
      
      // Only log if we have meaningful information
      if (hasMeaningfulInfo) {
        console.error("[FIRESTORE]", errorLog);
      }
    } else {
      // Log permission errors as warnings instead (expected during registration)
      console.warn("[FIRESTORE] Permission denied reading profile (expected during registration):", {
        userId: targetUserId,
        authenticated: !!auth.currentUser?.uid,
      });
    }
    
    // Return fallback profile so UI doesn't break
    return {
      ...DEFAULT_PROFILE,
      id: targetUserId,
      displayName: targetUserId ? `Korisnik ${targetUserId.substring(0, 8)}...` : "Korisnik",
    } as Profile;
  }
}

export async function saveProfile(p: Partial<Profile>): Promise<void> {
  // Use provided id if available (e.g., during registration), otherwise get from auth
  const userId = p.id || getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Verify that the userId matches the authenticated user (if auth is available)
  // This ensures Firestore security rules will allow the write
  // Note: During registration, auth.currentUser might not be set yet, so we allow it
  const currentAuthUser = auth.currentUser;
  if (currentAuthUser && currentAuthUser.uid && currentAuthUser.uid !== userId) {
    throw new Error("User ID mismatch: cannot save profile for different user");
  }

  const profileRef = doc(db, "profiles", userId);
  
  // Try to get existing profile, but if it doesn't exist (e.g., during registration),
  // start with a default profile that has the correct userId
  let current: Profile;
  let isNewProfile = false;
  try {
    current = await getProfile(userId);
    // Ensure the current profile has the correct id
    if (!current.id || current.id !== userId) {
      current = { ...current, id: userId };
    }
  } catch (error: any) {
    // If getProfile fails (e.g., permission denied during registration),
    // start with a default profile - this is expected for new users
    const errorCode = error?.code;
    if (errorCode === "permission-denied" || errorCode === "missing-or-insufficient-permissions") {
      // This is expected during registration - user doesn't have read permissions yet
      // but they should have write permissions for their own profile
      // Mark as new profile so we can use setDoc without merge
      isNewProfile = true;
      current = { id: userId, displayName: "Korisnik" };
    } else {
      // For other errors, log and use default profile
      const errorMsg = error?.message;
      if (errorMsg && typeof errorMsg === "string" && errorMsg.trim().length > 0) {
        console.warn("Could not fetch existing profile, creating new one:", errorMsg);
      }
      isNewProfile = true;
      current = { id: userId, displayName: "Korisnik" };
    }
  }
  
  const updated: Profile = { ...current, ...p, id: userId };
  
  // Always construct displayName from name/surname if they exist (prioritize them)
  // This ensures displayName is always up-to-date when name or surname changes
  if (updated.name) {
    if (updated.surname) {
      updated.displayName = `${updated.name} ${updated.surname}`.trim();
    } else {
      updated.displayName = updated.name.trim();
    }
  } else if (updated.username) {
    updated.displayName = updated.username.trim();
  } else if (!updated.displayName) {
    // Fallback: use existing displayName or default
    updated.displayName = current.displayName || "Korisnik";
  }

  // Prepare data for Firestore (convert timestamps)
  const firestoreData: any = {
    ...updated,
    memberSince: numberToTimestamp(updated.memberSince || Date.now()),
  };
  
  // Explicitly set displayName to ensure it's updated
  if (updated.displayName) {
    firestoreData.displayName = updated.displayName;
  }
  
  // For new profiles (during registration), use setDoc without merge
  // For existing profiles, use merge to update only changed fields
  // However, merge: true should work for both cases, but let's be explicit
  try {
    if (isNewProfile) {
      // For new profiles, create the document directly (no merge needed)
      await setDoc(profileRef, firestoreData);
    } else {
      // For existing profiles, merge to preserve other fields
      await setDoc(profileRef, firestoreData, { merge: true });
    }
  } catch (error: any) {
    const errorCode = error?.code;
    // If merge fails, try without merge (might be a new document)
    if (errorCode === "permission-denied" || errorCode === "missing-or-insufficient-permissions") {
      // Try without merge for new documents
      if (!isNewProfile) {
        try {
          await setDoc(profileRef, firestoreData);
        } catch (retryError: any) {
          // Re-throw with original error if retry also fails
          throw error;
        }
      } else {
        throw error;
      }
    } else {
      throw error;
    }
  }
}

export function getMySellerId(): string {
  return getCurrentUserId() || "anonymous";
}

// Get all user profiles (for users list)
export async function getAllProfiles(): Promise<Profile[]> {
  try {
    const profilesRef = collection(db, "profiles");
    const snapshot = await getDocs(profilesRef);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      memberSince: timestampToNumber(doc.data().memberSince),
    })) as Profile[];
  } catch (error: any) {
    // Silently handle permission errors - this is expected if user doesn't have read access
    if (error?.code === "permission-denied" || error?.message?.includes("permission")) {
      return [];
    }
    console.error("Error fetching all profiles:", error);
    return [];
  }
}

// ---------- FAVORITES ----------
// Favorites are now subcollections: profiles/{uid}/favorites/{listingId}
export async function getFavorites(): Promise<string[]> {
  const userId = getCurrentUserId();
  if (!userId) {
    return [];
  }

  try {
    const favoritesRef = collection(db, "profiles", userId, "favorites");
    const snapshot = await getDocs(favoritesRef);
    return snapshot.docs.map((doc) => doc.id);
  } catch (error: any) {
    // If permission error, user might not be fully authenticated yet
    if (error?.code === "permission-denied" || error?.code === "missing-or-insufficient-permissions") {
      console.warn("Permission denied for favorites - user might not be authenticated yet");
      return [];
    }
    console.error("Error fetching favorites:", error);
    return [];
  }
}

export async function setFavorites(ids: string[]): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Get current favorites
  const currentFavorites = await getFavorites();
  
  // Remove favorites that are no longer in the list
  const toRemove = currentFavorites.filter((id) => !ids.includes(id));
  for (const listingId of toRemove) {
    const favoriteRef = doc(db, "profiles", userId, "favorites", listingId);
    await deleteDoc(favoriteRef);
  }
  
  // Add new favorites
  const toAdd = ids.filter((id) => !currentFavorites.includes(id));
  for (const listingId of toAdd) {
    const favoriteRef = doc(db, "profiles", userId, "favorites", listingId);
    await setDoc(favoriteRef, { listingId, addedAt: Timestamp.now() });
  }
}

export async function isFavorite(listingId: string): Promise<boolean> {
  const userId = getCurrentUserId();
  if (!userId) return false;

  try {
    const favoriteRef = doc(db, "profiles", userId, "favorites", listingId);
    const favoriteSnap = await getDoc(favoriteRef);
    return favoriteSnap.exists();
  } catch (error) {
    console.error("Error checking favorite:", error);
    return false;
  }
}

export async function toggleFavorite(listingId: string): Promise<boolean> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  const favoriteRef = doc(db, "profiles", userId, "favorites", listingId);
  const favoriteSnap = await getDoc(favoriteRef);
  
  if (favoriteSnap.exists()) {
    // Remove favorite
    await deleteDoc(favoriteRef);
    return false;
  } else {
    // Add favorite
    await setDoc(favoriteRef, { listingId, addedAt: Timestamp.now() });
    return true;
  }
}

// ---------- REPORTS ----------
export async function getReports(): Promise<Report[]> {
  try {
    const reportsRef = collection(db, "reports");
    const snapshot = await getDocs(reportsRef);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: timestampToNumber(doc.data().createdAt),
    })) as Report[];
  } catch (error) {
    console.error("Error fetching reports:", error);
    return [];
  }
}

export async function addReport(report: Omit<Report, "id">): Promise<string> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  const reportRef = doc(collection(db, "reports"));
  await setDoc(reportRef, {
    ...report,
    reporterId: userId,
    status: "Open", // Default status
    createdAt: numberToTimestamp(report.createdAt || Date.now()),
  });
  return reportRef.id;
}

// Update report status (admin only)
export async function updateReportStatus(
  reportId: string,
  status: "Open" | "Resolved"
): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Verify admin status
  const adminRef = doc(db, "admins", userId);
  const adminSnap = await getDoc(adminRef);
  if (!adminSnap.exists()) {
    throw new Error("Only admins can update report status");
  }

  const reportRef = doc(db, "reports", reportId);
  const updateData: any = {
    status,
  };

  if (status === "Resolved") {
    updateData.resolvedAt = Timestamp.now();
    updateData.resolvedBy = userId;
  } else {
    // If reopening, clear resolved fields
    updateData.resolvedAt = deleteField();
    updateData.resolvedBy = deleteField();
  }

  await updateDoc(reportRef, updateData);
  
  // Log audit action
  if (status === "Resolved") {
    await addAuditLog({
      action: "resolve_report",
      targetType: "report",
      targetId: reportId,
      details: `Report marked as resolved`,
    });
  }
}

// ---------- ADMIN ACTIONS: HIDE LISTING ----------
export async function hideListing(listingId: string, reason?: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Verify admin status
  const adminRef = doc(db, "admins", userId);
  const adminSnap = await getDoc(adminRef);
  if (!adminSnap.exists()) {
    throw new Error("Only admins can hide listings");
  }

  // Get listing to get title for audit log
  const listingRef = doc(db, "listings", listingId);
  const listingSnap = await getDoc(listingRef);
  const listingData = listingSnap.data();

  // Update listing
  await updateDoc(listingRef, {
    hidden: true,
    hiddenAt: Timestamp.now(),
    hiddenBy: userId,
  });

  // Log audit action
  await addAuditLog({
    action: "hide_listing",
    targetType: "listing",
    targetId: listingId,
    targetName: listingData?.title || "Unknown listing",
    details: reason || "Listing hidden by admin",
  });
}

export async function unhideListing(listingId: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Verify admin status
  const adminRef = doc(db, "admins", userId);
  const adminSnap = await getDoc(adminRef);
  if (!adminSnap.exists()) {
    throw new Error("Only admins can unhide listings");
  }

  // Get listing to get title for audit log
  const listingRef = doc(db, "listings", listingId);
  const listingSnap = await getDoc(listingRef);
  const listingData = listingSnap.data();

  // Update listing
  await updateDoc(listingRef, {
    hidden: deleteField(),
    hiddenAt: deleteField(),
    hiddenBy: deleteField(),
  });

  // Log audit action
  await addAuditLog({
    action: "unhide_listing",
    targetType: "listing",
    targetId: listingId,
    targetName: listingData?.title || "Unknown listing",
    details: "Listing unhidden by admin",
  });
}

// ---------- ADMIN ACTIONS: SUSPEND USER ----------
export async function suspendUser(targetUserId: string, reason?: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");
  
  // Invalidate cache when blocking a user
  invalidateGloballyBlockedUsersCache();

  // Verify admin status
  const adminRef = doc(db, "admins", userId);
  const adminSnap = await getDoc(adminRef);
  if (!adminSnap.exists()) {
    throw new Error("Only admins can suspend users");
  }

  // Get profile to get display name for audit log
  const profileRef = doc(db, "profiles", targetUserId);
  const profileSnap = await getDoc(profileRef);
  const profileData = profileSnap.data();
  const displayName = profileData?.displayName || profileData?.name || profileData?.username || "Unknown user";

  // Update profile
  await updateDoc(profileRef, {
    suspended: true,
    suspendedAt: Timestamp.now(),
    suspendedBy: userId,
  });

  // Log audit action
  await addAuditLog({
    action: "suspend_user",
    targetType: "user",
    targetId: targetUserId,
    targetName: displayName,
    details: reason || "User suspended by admin",
  });
}

export async function unsuspendUser(targetUserId: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Verify admin status
  const adminRef = doc(db, "admins", userId);
  const adminSnap = await getDoc(adminRef);
  if (!adminSnap.exists()) {
    throw new Error("Only admins can unsuspend users");
  }
  
  // Invalidate cache when unblocking a user
  invalidateGloballyBlockedUsersCache();

  // Get profile to get display name for audit log
  const profileRef = doc(db, "profiles", targetUserId);
  const profileSnap = await getDoc(profileRef);
  const profileData = profileSnap.data();
  const displayName = profileData?.displayName || profileData?.name || profileData?.username || "Unknown user";

  // Update profile
  await updateDoc(profileRef, {
    suspended: deleteField(),
    suspendedAt: deleteField(),
    suspendedBy: deleteField(),
  });

  // Log audit action
  await addAuditLog({
    action: "unsuspend_user",
    targetType: "user",
    targetId: targetUserId,
    targetName: displayName,
    details: "User unsuspended by admin",
  });
}

// ---------- AUDIT LOG ----------
// adminId i adminName se uvijek dodaju automatski na osnovu trenutnog admin korisnika
async function addAuditLog(
  log: Omit<AuditLog, "id" | "createdAt" | "adminId" | "adminName">
): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Verify admin status
  const adminRef = doc(db, "admins", userId);
  const adminSnap = await getDoc(adminRef);
  if (!adminSnap.exists()) {
    throw new Error("Only admins can create audit logs");
  }

  // Get admin profile for name
  const adminProfileRef = doc(db, "profiles", userId);
  const adminProfileSnap = await getDoc(adminProfileRef);
  const adminProfileData = adminProfileSnap.data();
  const adminName = adminProfileData?.displayName || adminProfileData?.name || adminProfileData?.username || "Unknown admin";

  const auditLogRef = doc(collection(db, "auditLogs"));
  await setDoc(auditLogRef, {
    ...log,
    adminId: userId,
    adminName,
    createdAt: Timestamp.now(),
  });
}

export async function getAuditLogs(limitCount: number = 100): Promise<AuditLog[]> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Verify admin status
  const adminRef = doc(db, "admins", userId);
  const adminSnap = await getDoc(adminRef);
  if (!adminSnap.exists()) {
    throw new Error("Only admins can view audit logs");
  }

  try {
    const auditLogsRef = collection(db, "auditLogs");
    const q = query(auditLogsRef, orderBy("createdAt", "desc"), limit(limitCount));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: timestampToNumber(doc.data().createdAt),
    })) as AuditLog[];
  } catch (error) {
    console.error("Error fetching audit logs:", error);
    return [];
  }
}

// ---------- BLOCKS ----------
// Blocks are now subcollections: profiles/{uid}/blocks/{blockedUid}
export async function getBlockedUsers(): Promise<string[]> {
  const userId = getCurrentUserId();
  if (!userId) return [];

  try {
    const blocksRef = collection(db, "profiles", userId, "blocks");
    const snapshot = await getDocs(blocksRef);
    return snapshot.docs.map((doc) => doc.id);
  } catch (error) {
    console.error("Error fetching blocked users:", error);
    return [];
  }
}

export async function setBlockedUsers(userIds: string[]): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Get current blocks
  const currentBlocks = await getBlockedUsers();
  
  // Remove blocks that are no longer in the list
  const toRemove = currentBlocks.filter((id) => !userIds.includes(id));
  for (const blockedUid of toRemove) {
    const blockRef = doc(db, "profiles", userId, "blocks", blockedUid);
    await deleteDoc(blockRef);
  }
  
  // Add new blocks
  const toAdd = userIds.filter((id) => !currentBlocks.includes(id));
  for (const blockedUid of toAdd) {
    const blockRef = doc(db, "profiles", userId, "blocks", blockedUid);
    await setDoc(blockRef, { blockedUid, blockedAt: Timestamp.now() });
  }
}

export async function blockUser(targetUserId: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  const blockRef = doc(db, "profiles", userId, "blocks", targetUserId);
  const blockSnap = await getDoc(blockRef);
  
  if (!blockSnap.exists()) {
    await setDoc(blockRef, { blockedUid: targetUserId, blockedAt: Timestamp.now() });
  }
}

export async function unblockUser(targetUserId: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  const blockRef = doc(db, "profiles", userId, "blocks", targetUserId);
  const blockSnap = await getDoc(blockRef);
  
  if (blockSnap.exists()) {
    await deleteDoc(blockRef);
  }
}

// ---------- ADMIN FUNCTIONS ----------

// Initialize admins collection with email list
// Note: This should be run once manually via Firebase console or admin script
// To use: Manually create admins/{uid} documents in Firestore for each admin user
// Or call this function after getting the UID from Firebase Auth (requires server-side or manual setup)
export async function initializeAdmins(adminUids: string[]): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");
  
  // Check if current user is admin (to prevent unauthorized access)
  const adminRef = doc(db, "admins", userId);
  const adminSnap = await getDoc(adminRef);
  if (!adminSnap.exists()) {
    throw new Error("Only admins can initialize admin collection");
  }

  const batch = writeBatch(db);
  for (const uid of adminUids) {
    const adminDocRef = doc(db, "admins", uid);
    batch.set(adminDocRef, { 
      createdAt: Timestamp.now(),
      initializedBy: userId 
    });
  }
  await batch.commit();
}

// Admin-level block user (global block, not user-to-user)
export async function adminBlockUser(targetUserId: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Verify admin status
  const adminRef = doc(db, "admins", userId);
  const adminSnap = await getDoc(adminRef);
  if (!adminSnap.exists()) {
    throw new Error("Only admins can block users");
  }

  // Get profile to get display name for audit log
  const profileRef = doc(db, "profiles", targetUserId);
  const profileSnap = await getDoc(profileRef);
  const profileData = profileSnap.data();
  const displayName = profileData?.displayName || profileData?.name || profileData?.username || "Unknown user";

  const blockedUserRef = doc(db, "blockedUsers", targetUserId);
  await setDoc(blockedUserRef, { 
    blockedAt: Timestamp.now(),
    blockedBy: userId 
  });

  // Log audit action
  await addAuditLog({
    action: "block_user",
    targetType: "user",
    targetId: targetUserId,
    targetName: displayName,
    details: "User blocked by admin",
  });
}

// Admin-level unblock user
export async function adminUnblockUser(targetUserId: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Verify admin status
  const adminRef = doc(db, "admins", userId);
  const adminSnap = await getDoc(adminRef);
  if (!adminSnap.exists()) {
    throw new Error("Only admins can unblock users");
  }

  const blockedUserRef = doc(db, "blockedUsers", targetUserId);
  await deleteDoc(blockedUserRef);
}

// Check if user is globally blocked by admin
export async function isGloballyBlocked(userId: string): Promise<boolean> {
  try {
    const blockedUserRef = doc(db, "blockedUsers", userId);
    const blockedSnap = await getDoc(blockedUserRef);
    return blockedSnap.exists();
  } catch (error) {
    console.error("Error checking if user is globally blocked:", error);
    return false;
  }
}

// Get all globally blocked users (admin only)
export async function getGloballyBlockedUsers(): Promise<string[]> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Verify admin status
  const adminRef = doc(db, "admins", userId);
  const adminSnap = await getDoc(adminRef);
  if (!adminSnap.exists()) {
    throw new Error("Only admins can view globally blocked users");
  }

  try {
    const blockedUsersRef = collection(db, "blockedUsers");
    const snapshot = await getDocs(blockedUsersRef);
    return snapshot.docs.map((doc) => doc.id);
  } catch (error) {
    console.error("Error fetching globally blocked users:", error);
    return [];
  }
}

// ---------- USER ACCOUNT DELETION ----------

export async function deleteUserAccount(targetUserId?: string): Promise<void> {
  const currentUserId = getCurrentUserId();
  if (!currentUserId) throw new Error("User must be authenticated");

  // Determine which user to delete
  const userIdToDelete = targetUserId || currentUserId;

  // Only allow users to delete their own account (unless admin)
  if (userIdToDelete !== currentUserId) {
    // Check if current user is admin
    const adminRef = doc(db, "admins", currentUserId);
    const adminSnap = await getDoc(adminRef);
    if (!adminSnap.exists()) {
      throw new Error("You can only delete your own account");
    }
  }

  // 0. Upisi tombstone u deletedUsers kolekciju (da blokiramo buduca logovanja)
  try {
    const deletedRef = doc(db, "deletedUsers", userIdToDelete);
    await setDoc(deletedRef, {
      deletedAt: Timestamp.now(),
    }, { merge: true });
  } catch (error) {
    console.warn("Error writing deletedUsers tombstone:", error);
  }

  // 1. Hard delete profile dokumenta (i njegovih pod-kolekcija gdje je moguće)
  try {
    const profileRef = doc(db, "profiles", userIdToDelete);
    const profileSnap = await getDoc(profileRef);
    
    if (profileSnap.exists()) {
      // Obriši favorites podkolekciju (ako postoji)
      try {
        const favoritesRef = collection(db, "profiles", userIdToDelete, "favorites");
        const favoritesSnapshot = await getDocs(favoritesRef);
        const batch = writeBatch(db);
        favoritesSnapshot.docs.forEach((favDoc) => batch.delete(favDoc.ref));
        if (favoritesSnapshot.docs.length > 0) {
          await batch.commit();
        }
      } catch (subError) {
        console.warn("Error deleting favorites subcollection in profile delete:", subError);
      }

      // Na kraju, potpuno obriši profil dokument
      await deleteDoc(profileRef);
    }
  } catch (error) {
    console.warn("Error hard deleting profile:", error);
    // Nastavi sa ostalim čišćenjem čak i ako brisanje profila ne uspije
  }

  // 2. Anonymize user in all chat threads
  try {
    const chatsRef = collection(db, "chats");
    const chatsQuery = query(
      chatsRef,
      where("participants", "array-contains", userIdToDelete)
    );
    const chatsSnapshot = await getDocs(chatsQuery);

    const batch = writeBatch(db);
    let batchCount = 0;
    const MAX_BATCH_SIZE = 500;

    for (const chatDoc of chatsSnapshot.docs) {
      const chatData = chatDoc.data();
      const participantNames = { ...(chatData.participantNames || {}) };
      
      // Update participantNames to show "Deleted user"
      participantNames[userIdToDelete] = "Deleted user";
      
      batch.update(chatDoc.ref, {
        participantNames: participantNames,
      });
      
      batchCount++;
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        batchCount = 0;
      }
    }

    if (batchCount > 0) {
      await batch.commit();
    }
  } catch (error) {
    console.warn("Error anonymizing user in chats:", error);
    // Continue even if chat anonymization fails
  }

  // 3. Obriši sve oglase korisnika (hard delete iz kolekcije listings)
  try {
    const listingsRef = collection(db, "listings");
    const listingsQuery = query(
      listingsRef,
      where("sellerId", "==", userIdToDelete)
    );
    const listingsSnapshot = await getDocs(listingsQuery);

    const batch = writeBatch(db);
    let batchCount = 0;
    const MAX_BATCH_SIZE = 500;

    for (const listingDoc of listingsSnapshot.docs) {
      batch.delete(listingDoc.ref);
      batchCount++;
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        batchCount = 0;
      }
    }

    if (batchCount > 0) {
      await batch.commit();
    }
  } catch (error) {
    console.warn("Error deleting listings for deleted user:", error);
    // Nastavi čak i ako brisanje oglasa ne uspije
  }

  // 4. (Favorites cleanup je već obavljen u okviru brisanja profila)

  // 5. Obriši Firebase Auth korisnika (samo ako briše sam sebe)
  try {
    if (typeof window !== "undefined" && auth.currentUser && auth.currentUser.uid === userIdToDelete) {
      await auth.currentUser.delete();
    }
  } catch (error: any) {
    // Najčešća greška: requires-recent-login – korisnik se mora ponovo prijaviti prije brisanja naloga
    console.warn("Error deleting Firebase Auth user:", {
      code: error?.code,
      message: error?.message || String(error),
    });
    // Ne bacamo dalje grešku da ne spriječimo čišćenje Firestore podataka
  }
}

export async function isBlocked(userId: string): Promise<boolean> {
  const currentUserId = getCurrentUserId();
  if (!currentUserId) return false;

  try {
    const blockRef = doc(db, "profiles", currentUserId, "blocks", userId);
    const blockSnap = await getDoc(blockRef);
    return blockSnap.exists();
  } catch (error) {
    console.error("Error checking block:", error);
    return false;
  }
}

// ---------- REVIEWS ----------
export async function getReviews(
  targetType: "listing" | "user",
  targetId: string
): Promise<Review[]> {
  try {
    const reviewsRef = collection(db, "reviews");
    
    // Try with orderBy first, fallback to without if index doesn't exist
    let q;
    let snapshot;
    try {
      q = query(
        reviewsRef,
        where("targetType", "==", targetType),
        where("targetId", "==", targetId),
        orderBy("createdAt", "desc")
      );
      snapshot = await getDocs(q);
    } catch (orderByError: any) {
      // If orderBy fails (likely missing index), try without it
      if (orderByError?.code === "failed-precondition" || orderByError?.message?.includes("index")) {
        console.warn("[getReviews] OrderBy index missing, fetching without orderBy:", orderByError);
        q = query(
          reviewsRef,
          where("targetType", "==", targetType),
          where("targetId", "==", targetId)
        );
        snapshot = await getDocs(q);
      } else {
        throw orderByError;
      }
    }
    
    // Also do a direct query to verify we're getting all reviews
    // This helps catch any query issues
    const directQuery = query(
      reviewsRef,
      where("targetType", "==", targetType),
      where("targetId", "==", targetId)
    );
    const directSnapshot = await getDocs(directQuery);
    
    let reviews = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: timestampToNumber(data.createdAt),
      };
    }) as Review[];
    
    // Sort by createdAt descending (newest first) if we didn't use orderBy
    if (!reviews.some(r => r.createdAt === undefined)) {
      reviews.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    }
    
    // Verify we got the same count from both queries
    if (snapshot.docs.length !== directSnapshot.docs.length) {
      console.warn(`[getReviews] Query mismatch: orderBy query found ${snapshot.docs.length}, direct query found ${directSnapshot.docs.length}`);
    }
    
    // Log for debugging - show full details
    console.log(`[getReviews] Found ${reviews.length} reviews for ${targetType}:${targetId}`);
    console.log(`[getReviews] Query returned ${snapshot.docs.length} docs, direct query returned ${directSnapshot.docs.length} docs`);
    if (reviews.length > 0) {
      console.log(`[getReviews] Review details:`, reviews.map(r => ({ 
        id: r.id, 
        reviewerId: r.reviewerId || "unknown", 
        rating: r.rating, 
        comment: r.comment || "(no comment)",
        createdAt: r.createdAt,
        targetType: r.targetType,
        targetId: r.targetId
      })));
    } else {
      console.warn(`[getReviews] WARNING: No reviews found for ${targetType}:${targetId} - this might indicate a query issue`);
    }
    
    return reviews;
  } catch (error) {
    console.error("Error fetching reviews:", error);
    return [];
  }
}

export async function getUserReviews(userId: string): Promise<Review[]> {
  return getReviews("user", userId);
}

export async function getListingReviews(listingId: string): Promise<Review[]> {
  return getReviews("listing", listingId);
}

export async function addReview(review: Omit<Review, "id">): Promise<string> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Business rule: user-to-user ocjene može ostaviti samo kupac ili prodavač
  if (review.targetType === "user") {
    const targetUserId = review.targetId;

    const listingsRef = collection(db, "listings");
    const q = query(
      listingsRef,
      where("status", "==", "sold"),
      where("sellerId", "in", [userId, targetUserId])
    );
    const snap = await getDocs(q);
    const listings = snap.docs.map((d) => d.data() as Listing);

    const hasRelation = haveCompletedTransaction(userId, targetUserId, listings);

    if (!hasRelation) {
      throw new Error(
        "Možeš ocijeniti korisnika samo ako ste imali zajedničku kupovinu/prodaju."
      );
    }
  }

  // Check if review already exists from this user
  // IMPORTANT: We only update a review if it's from the same reviewer
  // This ensures all reviews from different reviewers are preserved
  const existing = await getReviews(review.targetType, review.targetId);
  const existingReview = existing.find((r) => String(r.reviewerId) === String(userId));
  
  // Log for debugging - show full details
  console.log("[addReview] Target:", review.targetType, review.targetId);
  console.log("[addReview] Reviewer:", userId);
  console.log("[addReview] Existing reviews count:", existing.length);
  console.log("[addReview] Existing reviews from all reviewers:", existing.map(r => ({ 
    id: r.id,
    reviewerId: r.reviewerId || "unknown", 
    rating: r.rating,
    comment: r.comment || "(no comment)",
    createdAt: r.createdAt
  })));
  console.log("[addReview] Existing review from this user:", existingReview ? `Yes (ID: ${existingReview.id}, rating: ${existingReview.rating}, will update)` : "No (will create new)");
  
  // Only update if it's the same reviewer's review
  const reviewRef = existingReview
    ? doc(db, "reviews", existingReview.id)
    : doc(collection(db, "reviews"));
  
  // Safety check: ensure we're not accidentally updating someone else's review
  if (existingReview && String(existingReview.reviewerId) !== String(userId)) {
    console.error("[addReview] ERROR: Attempted to update review from different reviewer!");
    throw new Error("Cannot update review from different reviewer");
  }

  // Add listingId field for easier querying when targetType is "listing"
  const reviewData: any = {
    ...review,
    reviewerId: userId,
    createdAt: numberToTimestamp(review.createdAt || Date.now()),
  };
  
  if (review.targetType === "listing") {
    reviewData.listingId = review.targetId;
  }

  await setDoc(reviewRef, reviewData);
  
  // Verify all reviews are still there after saving
  const allReviewsAfter = await getReviews(review.targetType, review.targetId);
  console.log("[addReview] Total reviews after save:", allReviewsAfter.length);
  console.log("[addReview] All review ratings after save:", allReviewsAfter.map(r => ({ 
    id: r.id,
    reviewerId: r.reviewerId || "unknown", 
    rating: r.rating,
    comment: r.comment || "(no comment)"
  })));
  
  // Critical check: ensure we didn't lose any reviews from different reviewers
  const reviewersBefore = new Set(existing.map(r => r.reviewerId).filter(Boolean));
  const reviewersAfter = new Set(allReviewsAfter.map(r => r.reviewerId).filter(Boolean));
  const lostReviewers = Array.from(reviewersBefore).filter(r => !reviewersAfter.has(r));
  
  if (lostReviewers.length > 0) {
    console.error("[addReview] CRITICAL ERROR: Lost reviews from reviewers:", lostReviewers);
    console.error("[addReview] This should NEVER happen - reviews from different reviewers should be preserved!");
  } else if (existing.length > 1 && allReviewsAfter.length < existing.length) {
    console.error("[addReview] ERROR: Lost reviews! Had", existing.length, "now have", allReviewsAfter.length);
  } else if (existing.length > 0 && allReviewsAfter.length === existing.length + 1) {
    console.log("[addReview] SUCCESS: Added new review (different reviewer)");
  } else if (existingReview && allReviewsAfter.length === existing.length) {
    console.log("[addReview] SUCCESS: Updated existing review from same reviewer");
  }
  
  // Critical check: ensure we didn't lose any reviews
  if (existing.length > 1 && allReviewsAfter.length < existing.length) {
    console.error("[addReview] ERROR: Lost reviews! Had", existing.length, "now have", allReviewsAfter.length);
    console.error("[addReview] Missing reviewers:", 
      existing.filter(e => !allReviewsAfter.find(a => a.reviewerId === e.reviewerId)).map(r => r.reviewerId)
    );
  }

  // Create notification for rating
  try {
    let recipientUserId: string | null = null;
    
    if (review.targetType === "user") {
      // If rating is for a user, notify that user
      recipientUserId = review.targetId;
    } else if (review.targetType === "listing") {
      // If rating is for a listing, notify the listing owner
      try {
        const listingRef = doc(db, "listings", review.targetId);
        const listingSnap = await getDoc(listingRef);
        if (listingSnap.exists()) {
          const listing = listingSnap.data() as Listing;
          recipientUserId = listing.sellerId;
        }
      } catch (error) {
        console.error("Error getting listing for notification:", error);
      }
    }

    if (recipientUserId && recipientUserId !== userId) {
      const { createRatingNotification, createRatingReminderNotification } = await import("./notifications");
      
      // If seller is rating buyer (user-to-user rating), only create reminder (not regular rating notification)
      if (review.targetType === "user") {
        // Check if this is a seller rating a buyer (they had a transaction)
        try {
          const listingsRef = collection(db, "listings");
          const q = query(
            listingsRef,
            where("status", "==", "sold"),
            where("sellerId", "==", userId),
            where("buyerId", "==", recipientUserId)
          );
          const snap = await getDocs(q);
          
          if (snap.docs.length > 0) {
            // This is a seller rating a buyer - only create reminder notification (skip regular rating notification)
            const listing = snap.docs[0].data() as Listing;
            await createRatingReminderNotification(
              recipientUserId, // buyer
              userId, // seller
              listing.id || "",
              listing.title || "Oglas",
              review.rating
            );
          } else {
            // Not a seller-buyer transaction, create regular rating notification
            await createRatingNotification(
              recipientUserId,
              userId,
              review.rating,
              review.comment,
              review.targetType,
              review.targetId
            );
          }
        } catch (reminderError) {
          console.error("Error creating rating reminder notification:", reminderError);
          // Fallback to regular rating notification if reminder fails
          await createRatingNotification(
            recipientUserId,
            userId,
            review.rating,
            review.comment,
            review.targetType,
            review.targetId
          );
        }
      } else {
        // For listing ratings or other types, create regular rating notification
        await createRatingNotification(
          recipientUserId,
          userId,
          review.rating,
          review.comment,
          review.targetType,
          review.targetId
        );
      }
    }
  } catch (notifError) {
    // Silently fail - notification creation shouldn't break review creation
    console.error("Error creating rating notification:", notifError);
  }

  return reviewRef.id;
}

export async function getAverageRating(
  targetType: "listing" | "user",
  targetId: string
): Promise<number> {
  const reviews = await getReviews(targetType, targetId);
  const avg = calculateAverageRating(reviews);
  console.log(`[getAverageRating] Calculated average ${avg} from ${reviews.length} reviews for ${targetType}:${targetId}`);
  return avg;
}

// ---------- CHAT MESSAGES ----------
export type ChatMessage = {
  id: string;
  from: string;
  text: string;
  createdAt: number; // Changed from ts to createdAt for consistency
  delivered?: boolean;
  read?: boolean;
  imageUrl?: string; // URL of attached image
};

export type ChatMessagesResult = {
  messages: ChatMessage[];
  lastDoc?: QueryDocumentSnapshot<DocumentData>;
  hasMore: boolean;
};

// Get chat messages with pagination (DESC order, newest first, limit 50 - WhatsApp pattern)
export async function getChatMessages(
  threadKey: string,
  lastDoc?: QueryDocumentSnapshot<DocumentData>
): Promise<ChatMessagesResult> {
  try {
    // Guard: don't hit Firestore if user nije autentifikovan (može se desiti
    // kratko nakon mount-a prije nego što auth state bude spreman)
    const userId = getCurrentUserId();
    if (!userId) {
      return { messages: [], hasMore: false };
    }

    const messagesRef = collection(db, "chats", threadKey, "messages");
    // Use DESC order (newest first) - UI will reverse to show naturally
    let q = query(messagesRef, orderBy("createdAt", "desc"), limit(50));
    
    if (lastDoc) {
      // For loading older messages, use startAfter with DESC (lastDoc is the oldest in current set)
      q = query(messagesRef, orderBy("createdAt", "desc"), startAfter(lastDoc), limit(50));
    }
    
    const snapshot = await getDocs(q);
    const messages = snapshot.docs
      .map((doc) => {
        const data = doc.data();
        // Support both ts (old) and createdAt (new) for backward compatibility
        const timestamp = data.createdAt || data.ts;
        const deletedFor = data.deletedFor || {};
        return {
          id: doc.id,
          from: String(data.from || ""),
          text: String(data.text || ""),
          createdAt: timestampToNumber(timestamp),
          delivered: data.delivered !== undefined ? Boolean(data.delivered) : false,
          read: data.read !== undefined ? Boolean(data.read) : false,
          imageUrl: data.imageUrl || undefined,
          deletedFor: deletedFor,
          deletedForEveryone: data.deletedForEveryone || false,
        } as ChatMessage & { deletedFor?: Record<string, boolean>; deletedForEveryone?: boolean };
      })
      .filter((msg) => {
        // Filter out messages deleted for current user
        return !msg.deletedFor?.[userId];
      });
    // Messages are in DESC order (newest first) - UI will reverse for display

    // lastDoc is the last (oldest) document in DESC order
    const newLastDoc = snapshot.docs[snapshot.docs.length - 1];
    const hasMore = snapshot.docs.length === 50;

    return { messages, lastDoc: newLastDoc, hasMore };
  } catch (error: any) {
    // Only log if there's an actual error
    if (error) {
      console.error("[FIRESTORE] Error fetching chat messages:", {
        action: "fetchChatMessages",
        threadKey,
        path: `chats/${threadKey}/messages`,
        error: error?.toString?.() || error,
        code: error?.code,
        message: error?.message,
        stack: error?.stack,
      });
    }
    return { messages: [], hasMore: false };
  }
}

// Subscribe to chat messages (DESC order, newest first, limit 50 for real-time - WhatsApp pattern)
export function subscribeToChatMessages(
  threadKey: string,
  userId: string,
  callback: (messages: (ChatMessage & { deletedFor?: Record<string, boolean>; deletedForEveryone?: boolean })[]) => void
): () => void {
  const messagesRef = collection(db, "chats", threadKey, "messages");
  // Use DESC order (newest first) - UI will reverse to show naturally
  const q = query(messagesRef, orderBy("createdAt", "desc"), limit(50));
  
  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const messages = snapshot.docs
        .map((doc) => {
          const data = doc.data();
          // Support both ts (old) and createdAt (new) for backward compatibility
          const timestamp = data.createdAt || data.ts;
          const deletedFor = data.deletedFor || {};
          return {
            id: doc.id,
            from: String(data.from || ""),
            text: String(data.text || ""),
            createdAt: timestampToNumber(timestamp),
            delivered: data.delivered !== undefined ? Boolean(data.delivered) : false,
            read: data.read !== undefined ? Boolean(data.read) : false,
            imageUrl: data.imageUrl || undefined,
            deletedFor: deletedFor,
            deletedForEveryone: data.deletedForEveryone || false,
          } as ChatMessage & { deletedFor?: Record<string, boolean>; deletedForEveryone?: boolean };
        })
        .filter((msg) => {
          // Filter out messages deleted for current user
          return !msg.deletedFor?.[userId];
        });
      // Messages are in DESC order (newest first) - UI will reverse for display
      callback(messages);
    },
    (error: any) => {
      // Nekad onSnapshot callback dobije "prazan" error ili samo warning.
      // U tom slučaju ne želimo spamati konzolu.
      if (!error || typeof error !== "object") {
        callback([]);
        return;
      }

      // Samo prave Firestore greške logujemo (izbjegavamo failed-precondition / unavailable)
      if (error.code !== "failed-precondition" && error.code !== "unavailable") {
        console.error("[FIRESTORE] Error subscribing to chat messages:", {
          action: "subscribeToChatMessages",
          threadKey,
          userId,
          path: `chats/${threadKey}/messages`,
          error: error?.toString?.() || error,
          code: error?.code,
          message: error?.message,
          stack: error?.stack,
        });
      }
      callback([]);
    }
  );

  return unsubscribe;
}

// Rate limiting for messages (anti-spam)
// Store message timestamps in memory (client-side) - in production, use server-side rate limiting
const messageRateLimits: Map<string, number[]> = new Map();
const RATE_LIMIT_MESSAGES = 10; // Max messages
const RATE_LIMIT_WINDOW = 60000; // 1 minute in milliseconds

function checkRateLimit(userId: string, threadKey: string): boolean {
  const key = `${userId}_${threadKey}`;
  const now = Date.now();
  const timestamps = messageRateLimits.get(key) || [];
  
  // Remove timestamps outside the window
  const recentTimestamps = timestamps.filter(ts => now - ts < RATE_LIMIT_WINDOW);
  
  if (recentTimestamps.length >= RATE_LIMIT_MESSAGES) {
    return false; // Rate limit exceeded
  }
  
  // Add current timestamp
  recentTimestamps.push(now);
  messageRateLimits.set(key, recentTimestamps);
  
  // Clean up old entries periodically
  if (messageRateLimits.size > 1000) {
    // Keep only recent entries
    for (const [k, ts] of messageRateLimits.entries()) {
      const recent = ts.filter(t => now - t < RATE_LIMIT_WINDOW);
      if (recent.length === 0) {
        messageRateLimits.delete(k);
      } else {
        messageRateLimits.set(k, recent);
      }
    }
  }
  
  return true; // Rate limit OK
}

export async function sendChatMessage(
  threadKey: string,
  message: Omit<ChatMessage, "id">
): Promise<string> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Validate message data
  console.log("sendChatMessage called with:", { threadKey, message, userId });
  if (!message) {
    throw new Error("Message is required");
  }
  // Allow messages with either text or image
  if ((!message.text || !message.text.trim()) && !message.imageUrl) {
    throw new Error("Message must have text or image");
  }

  // Rate limiting check
  if (!checkRateLimit(userId, threadKey)) {
    throw new Error(`Previše poruka. Molimo sačekajte prije slanja nove poruke. (Max ${RATE_LIMIT_MESSAGES} poruka/min)`);
  }

  try {
    // Get or create chat thread with participants array
    const chatRef = doc(db, "chats", threadKey);
    const chatSnap = await getDoc(chatRef);
    
    let participants: string[] = [];
    if (chatSnap.exists()) {
      // Get participants from existing document
      const data = chatSnap.data();
      participants = data.participants && Array.isArray(data.participants)
        ? data.participants
        : [];
    }

    // UVIJEK dodatno parsiraj učesnike iz threadKey-a i spoji ih,
    // da se isprave stari threadovi koji su kreirani samo sa jednim userId-om.
    // Format ključa:
    //  - novi:  nosiva_chat_userId1__userId2
    //  - stari: nosiva_chat_userId1__userId2_listingId
    const rest = threadKey.replace("nosiva_chat_", "");
    let fromKey: string[] = [];

    if (rest.includes("__")) {
      const firstSep = rest.indexOf("__");
      const uid1 = rest.substring(0, firstSep);
      let tail = rest.substring(firstSep + 2); // nakon "__"

      // Ako postoji listing suffix, on počinje sa "_" nakon drugog UID-a
      const listingSep = tail.indexOf("_");
      const uid2 = listingSep >= 0 ? tail.substring(0, listingSep) : tail;

      fromKey = [uid1, uid2].filter(Boolean);
    } else if (rest) {
      fromKey = [rest];
    }

    participants = Array.from(new Set([...(participants || []), ...fromKey]));
    
    // Ensure userId is in participants
    if (!participants.includes(userId)) {
      participants.push(userId);
    }
    const sortedParticipants = [...participants].sort();

    // Get participant names and avatars for participantNames and participantAvatars maps
    const participantNames: Record<string, string> = {};
    const participantAvatars: Record<string, string> = {};
    const existingData = chatSnap.exists() ? chatSnap.data() : {};
    const existingParticipantNames = existingData.participantNames || {};
    const existingParticipantAvatars = existingData.participantAvatars || {};
    
    // Load names and avatars for all participants (use existing if available, otherwise fetch)
    await Promise.all(
      sortedParticipants.map(async (pid) => {
        if (existingParticipantNames[pid]) {
          participantNames[pid] = existingParticipantNames[pid];
          participantAvatars[pid] = existingParticipantAvatars[pid] || "";
        } else {
          try {
            const profile = await getProfile(pid);
            participantNames[pid] = getDisplayName(profile, pid);
            participantAvatars[pid] = getAvatarUrl(profile);
          } catch (error) {
            participantNames[pid] = `Korisnik ${pid.substring(0, 8)}...`;
            participantAvatars[pid] = "";
          }
        }
      })
    );

    // Determine receiver (other participant)
    const receiverId = sortedParticipants.find((p) => p !== userId) || userId;
    const senderId = userId;

    // Check if user is blocked (prevent sending messages to blocked users)
    if (receiverId && receiverId !== userId) {
      try {
        const isUserBlocked = await isBlocked(receiverId);
        if (isUserBlocked) {
          throw new Error("Ne možete slati poruke blokiranom korisniku");
        }
        
        // Also check if receiver has blocked the sender
        const receiverBlockRef = doc(db, "profiles", receiverId, "blocks", userId);
        const receiverBlockSnap = await getDoc(receiverBlockRef);
        if (receiverBlockSnap.exists()) {
          throw new Error("Ovaj korisnik vas je blokirao");
        }
        
        // Check global blocks
        const isGloballyBlockedSender = await isGloballyBlocked(userId);
        const isGloballyBlockedReceiver = await isGloballyBlocked(receiverId);
        if (isGloballyBlockedSender || isGloballyBlockedReceiver) {
          throw new Error("Ne možete slati poruke");
        }
      } catch (error: any) {
        if (error.message && error.message.includes("blokira")) {
          throw error; // Re-throw block-related errors
        }
        console.warn("Error checking block status:", error);
        // Continue if it's not a block-related error
      }
    }

    // Prepare message data
    const messageText = String(message.text || "").trim();
    const messageFrom = String(message.from || userId);
    // Use image URL or text for lastMessageText
    const lastMessageText = message.imageUrl ? "📷 Slika" : messageText;
    
    // Add message - ensure all required fields are set
    const messagesRef = collection(db, "chats", threadKey, "messages");
    const messageRef = doc(messagesRef);
    
    // delivered starts as false, will be updated after message is successfully saved
    const messageData: any = {
      from: messageFrom,
      text: messageText || "",
      createdAt: numberToTimestamp(message.createdAt || Date.now()),
      read: false,
      delivered: false, // Start as false, update after save
    };

        // Add imageUrl if present
    if (message.imageUrl) {
      messageData.imageUrl = message.imageUrl;
    }

    // Validate message data before sending
    if (!messageData.from || (!messageData.text && !messageData.imageUrl) || !messageData.createdAt) {
      throw new Error("Invalid message data");
    }

    console.log("Creating message with data:", messageData);
    console.log("Thread exists:", chatSnap.exists());
    console.log("Participants:", sortedParticipants);
    
    await setDoc(messageRef, messageData);

    // Update delivered status after message is successfully saved
    // This allows the sender to update their own message's delivered status
    try {
      await updateDoc(messageRef, { delivered: true });
    } catch (updateError: any) {
      // If update fails, message is still saved, just delivered status might not update
      console.error("[FIRESTORE]", {
        action: "updateDeliveredStatus",
        threadKey,
        messageId: messageRef.id,
        path: `chats/${threadKey}/messages/${messageRef.id}`,
        error: updateError,
        code: updateError?.code,
        message: updateError?.message,
      });
    }

    // Check if message contains an offer and create notification
    if (messageText && receiverId && receiverId !== userId) {
      try {
        // Parse offer from message text
        const lines = messageText.trim().split("\n").filter((line) => line.trim());
        if (lines.length >= 3) {
          const title = lines[0].trim();
          const amountMatch = lines[1].match(/([\d,\.]+)\s*KM/);
          const urlLine = lines[lines.length - 1];
          const marketplaceMatch = urlLine.match(/marketplace\/([a-zA-Z0-9]+)/);
          
          if (amountMatch && marketplaceMatch) {
            const listingId = marketplaceMatch[1];
            const offerAmount = parseFloat(amountMatch[1].replace(",", "."));
            
            // Get listing to get title
            try {
              const listingRef = doc(db, "listings", listingId);
              const listingSnap = await getDoc(listingRef);
              if (listingSnap.exists()) {
                const listing = listingSnap.data() as Listing;
                const { createOfferNotification } = await import("./notifications");
                await createOfferNotification(
                  receiverId,
                  userId,
                  messageText,
                  listingId,
                  listing.title,
                  offerAmount,
                  messageRef.id
                );
              }
            } catch (notifError) {
              console.error("Error creating offer notification:", notifError);
            }
          }
        }
      } catch (offerError) {
        // Silently fail - notification creation shouldn't break message sending
        console.error("Error processing offer notification:", offerError);
      }
    }

    // Update chat doc with lastMessageText, lastMessageFrom, unread counts, and remove deletedFor
    if (!chatSnap.exists()) {
      // New thread: initialize everything
      await setDoc(chatRef, {
        lastUpdated: Timestamp.now(),
        participants: sortedParticipants,
        participantNames: participantNames,
        participantAvatars: participantAvatars,
        lastMessageText: lastMessageText,
        lastMessageFrom: messageFrom,
        unread: {
          [receiverId]: 1,
          [senderId]: 0,
        },
      });
    } else {
      // Existing thread: update lastMessageText, lastMessageFrom, unread counts
      // i OSVJEŽI participants da sigurno sadrži obje strane (migra legacy threadove).
      const updateData: any = {
        lastUpdated: Timestamp.now(),
        participants: sortedParticipants,
        participantNames: participantNames,
        participantAvatars: participantAvatars,
        lastMessageText: lastMessageText,
        lastMessageFrom: messageFrom,
        [`unread.${receiverId}`]: increment(1),
        [`unread.${senderId}`]: 0,
      };
      
      // Remove deletedFor flag za primaoca i pošiljaoca
      // Ako je neko od njih ranije "obrisao" razgovor (soft delete),
      // nova poruka treba ponovo vratiti thread u inbox.
      const existingData = chatSnap.data();
      if (existingData.deletedFor?.[receiverId]) {
        updateData[`deletedFor.${receiverId}`] = deleteField();
      }
      if (existingData.deletedFor?.[senderId]) {
        updateData[`deletedFor.${senderId}`] = deleteField();
      }
      
      await updateDoc(chatRef, updateData);
    }

    // Pošalji email obavještenje primaocu (asinhrono, ne blokira slanje poruke)
    if (receiverId && receiverId !== userId) {
      // Ne čekaj email slanje - pošalji asinhrono
      (async () => {
        try {
          console.log(`[EMAIL] Starting email notification process for receiver: ${receiverId}`);
          
          // Provjeri da li primaoc ima omogućene notifikacije
          let notificationsEnabled = true; // Default: omogućeno
          try {
            const receiverProfile = await getProfile(receiverId);
            const settings = receiverProfile.notificationSettings;
            
            // Provjeri postavke notifikacija
            // Ako je disableAll true, notifikacije su onemogućene
            if (settings?.disableAll) {
              notificationsEnabled = false;
              console.log(`[EMAIL] ⏭️  Notifications disabled for receiver (disableAll=true)`);
            }
            // Ako je enabled eksplicitno false, notifikacije su onemogućene
            else if (settings?.enabled === false) {
              notificationsEnabled = false;
              console.log(`[EMAIL] ⏭️  Notifications disabled for receiver (enabled=false)`);
            }
            // Inače su omogućene (default)
            else {
              notificationsEnabled = true;
              console.log(`[EMAIL] ✅ Notifications enabled for receiver`);
            }
          } catch (settingsError: any) {
            console.warn("[EMAIL] ⚠️  Could not check notification settings, defaulting to enabled:", settingsError.message);
            // Default: omogućeno ako ne možemo provjeriti
            notificationsEnabled = true;
          }
          
          // Ako su notifikacije onemogućene, preskoči slanje emaila
          if (!notificationsEnabled) {
            console.log(`[EMAIL] ⏭️  Skipping email notification - notifications are disabled for receiver ${receiverId}`);
            return;
          }
          
          // Uzmi email primaoca iz profila
          let receiverEmail: string | undefined;
          try {
            console.log(`[EMAIL] 🔍 Fetching profile for receiver: ${receiverId}`);
            const receiverProfile = await getProfile(receiverId);
            console.log(`[EMAIL] 📋 Profile data:`, {
              hasEmail: !!receiverProfile.email,
              email: receiverProfile.email || 'undefined',
              profileKeys: Object.keys(receiverProfile)
            });
            receiverEmail = receiverProfile.email;
            console.log(`[EMAIL] ${receiverEmail ? '✅' : '❌'} Email from profile: ${receiverEmail || 'not found'}`);
          } catch (profileError: any) {
            console.warn("[EMAIL] ❌ Error getting receiver profile:", {
              error: profileError.message,
              receiverId: receiverId
            });
          }
          
          // Ako email nije u profilu, pokušaj dobiti iz API rute (koja koristi Admin SDK)
          if (!receiverEmail) {
            console.log(`[EMAIL] 📧 Email not in profile, trying to get from Firebase Auth via API...`);
            try {
              // U browser-u koristimo relativni path (izbjegava CORS i dupli slash)
              // U server-side kontekstu, koristimo environment varijablu
              const apiUrl = typeof window !== "undefined" 
                ? `/api/get-user-email?userId=${receiverId}`  // Relativni path u browser-u
                : `${process.env.NEXT_PUBLIC_BASE_URL || "https://nosiva.ba"}/api/get-user-email?userId=${receiverId}`;
              
              console.log(`[EMAIL] 🔗 Calling get-user-email API: ${apiUrl}`);
              
              const response = await fetch(apiUrl, {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                },
              });
              
              console.log(`[EMAIL] 📊 API response status: ${response.status}`);
              
              if (response.ok) {
                const data = await response.json();
                console.log(`[EMAIL] 📦 API response data:`, data);
                receiverEmail = data.email || null;
                console.log(`[EMAIL] ${receiverEmail ? '✅' : '❌'} Email from API: ${receiverEmail || 'not found'}`);
                if (data.note) {
                  console.log(`[EMAIL] ℹ️  API note: ${data.note}`);
                  if (data.note.includes("nije konfiguriran")) {
                    console.log(`[EMAIL] 💡 Za automatsko dobijanje emaila iz Firebase Auth:`);
                    console.log(`[EMAIL]    Pokrenite: npm run setup:firebase-admin`);
                  }
                }
                
                // Ako smo dobili email iz Auth, spremi ga u profil za buduće
                if (receiverEmail) {
                  try {
                    const profileRef = doc(db, "profiles", receiverId);
                    await updateDoc(profileRef, { email: receiverEmail });
                    console.log(`[EMAIL] ✅ Email saved to profile for future use`);
                  } catch (saveError: any) {
                    console.warn("[EMAIL] ⚠️  Could not save email to profile:", saveError.message);
                  }
                }
              } else {
                const errorText = await response.text().catch(() => "");
                console.warn(`[EMAIL] ❌ API error (${response.status}): ${errorText.substring(0, 200)}`);
              }
            } catch (apiError: any) {
              console.warn("[EMAIL] ❌ Email API error:", apiError.message || "Unknown error");
              if (apiError.message?.includes("Failed to fetch") || apiError.message?.includes("CORS")) {
                console.log(`[EMAIL] 💡 Provjerite da li je Next.js server pokrenut (npm run dev)`);
              }
            }
          }
          
          if (receiverEmail) {
            console.log(`[EMAIL] Sending email notification to: ${receiverEmail}`);
            
            // Uzmi ime pošiljatelja
            const senderName = participantNames[senderId] || `Korisnik ${senderId.substring(0, 8)}...`;
            
            // Kreiraj URL za chat (koristi base URL iz environment varijable ili default)
            const baseUrl = typeof window !== "undefined" 
              ? window.location.origin
              : (process.env.NEXT_PUBLIC_BASE_URL || "https://nosiva.ba");
            const chatUrl = `${baseUrl}/chat?withId=${receiverId}&with=${encodeURIComponent(senderName)}`;
            
            // U browser-u koristimo relativni path za API (izbjegava CORS)
            const emailApiUrl = typeof window !== "undefined"
              ? "/api/send-email-notification"  // Relativni path
              : `${baseUrl}/api/send-email-notification`;
            
            console.log(`[EMAIL] Calling send-email-notification API: ${emailApiUrl}`);
            console.log(`[EMAIL] Email data:`, {
              recipientEmail: receiverEmail,
              senderName: senderName,
              messagePreview: lastMessageText.substring(0, 50)
            });
            
            // Pozovi API rutu za slanje email obavještenja
            try {
              const response = await fetch(emailApiUrl, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  recipientEmail: receiverEmail,
                  senderName: senderName,
                  messagePreview: lastMessageText,
                  chatUrl: chatUrl,
                }),
              });
              
              console.log(`[EMAIL] API response status: ${response.status}`);
              
              if (response.ok) {
                const result = await response.json();
                console.log(`[EMAIL] ✅ Email notification sent successfully:`, result);
              } else {
                const errorText = await response.text().catch(() => "");
                console.warn(`[EMAIL] ❌ Email notification failed (${response.status}):`, errorText.substring(0, 200));
              }
            } catch (fetchError: any) {
              console.error("[EMAIL] ❌ Email notification fetch error:", fetchError.message || "Unknown error", fetchError);
            }
          } else {
            console.log(`[EMAIL] ⏭️  Email not found for recipient ${receiverId}, skipping email notification`);
            console.log(`[EMAIL] 💡 Rješenje:`);
            console.log(`[EMAIL]    1. Dodajte email u profil korisnika ${receiverId} u Firestore`);
            console.log(`[EMAIL]    2. Ili konfigurirajte Firebase Admin SDK da automatski dobija email iz Firebase Auth`);
            console.log(`[EMAIL]       (Postavite FIREBASE_SERVICE_ACCOUNT_KEY u .env.local)`);
            console.log(`[EMAIL]       Pokrenite: npm run setup:firebase-admin`);
          }
        } catch (emailError) {
          // Ne prikazuj grešku korisniku - email obavještenje je opcionalno
          console.warn("Error sending email notification:", emailError);
        }
      })();
    }

    return messageRef.id;
  } catch (error: any) {
    console.error("[FIRESTORE]", {
      action: "sendChatMessage",
      userId,
      threadKey,
      path: `chats/${threadKey}/messages`,
      error: error,
      code: error?.code,
      message: error?.message,
    });
    throw error;
  }
}

// ✅ getChatThreads - samo user-ovi threadovi
export async function getChatThreads(
  userId: string
): Promise<
  Array<{
    threadKey: string;
    lastMessageText?: string;
    lastMessageFrom?: string;
    participants: string[];
    title?: string;
    participantNames?: Record<string, string>;
    participantAvatars?: Record<string, string>;
    unread?: Record<string, number>;
    deletedFor?: Record<string, boolean>;
    lastUpdated?: number;
  }>
> {
  try {
    const chatsRef = collection(db, "chats");
    const q = query(
      chatsRef,
      where("participants", "array-contains", userId),
      orderBy("lastUpdated", "desc")
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((docSnap) => {
      const data = docSnap.data() || {};
      return {
        threadKey: docSnap.id,
        lastMessageText: data.lastMessageText,
        lastMessageFrom: data.lastMessageFrom,
        participants: Array.isArray(data.participants) ? data.participants : [],
        title: data.title,
        participantNames: data.participantNames || {},
        participantAvatars: data.participantAvatars || {},
        unread: data.unread || {},
        deletedFor: data.deletedFor || {},
        lastUpdated: data.lastUpdated?.toMillis
          ? data.lastUpdated.toMillis()
          : undefined,
      };
    });
  } catch (error: any) {
    const code = error?.code;
    // Permission greške (npr. korisnik nije logovan) su očekivane – ne loguj kao fatalne
    if (code !== "permission-denied" && code !== "missing-insufficient-permissions" && code !== "missing-or-insufficient-permissions") {
      console.error("Error fetching chat threads:", error);
    }
    return [];
  }
}

// ✅ subscribeToChatThreads - real-time samo za user-ove threadove
export function subscribeToChatThreads(
  userId: string,
  callback: (threads: Array<{
    threadKey: string;
    lastMessageText?: string;
    lastMessageFrom?: string;
    participants: string[];
    title?: string;
    participantNames?: Record<string, string>;
    participantAvatars?: Record<string, string>;
    unread?: Record<string, number>;
    deletedFor?: Record<string, boolean>;
    lastUpdated?: number;
  }>) => void
): () => void {
  if (!userId || typeof userId !== "string" || userId.trim().length === 0) {
    callback([]);
    return () => {};
  }

  try {
    const chatsRef = collection(db, "chats");
    const q = query(
      chatsRef,
      where("participants", "array-contains", userId),
      orderBy("lastUpdated", "desc")
    );

    const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const threads = snapshot.docs
        .filter((d) => {
          const data = d.data() || {};
          const deletedFor = data.deletedFor || {};
          return !deletedFor[userId];
        })
        .map((d) => {
          const data = d.data() || {};
          return {
            threadKey: d.id,
            lastMessageText: data.lastMessageText,
            lastMessageFrom: data.lastMessageFrom,
            participants: Array.isArray(data.participants) ? data.participants : [],
            title: data.title,
            participantNames: data.participantNames || {},
            participantAvatars: data.participantAvatars || {},
            unread: data.unread || {},
            deletedFor: data.deletedFor || {},
            lastUpdated: data.lastUpdated?.toMillis
              ? data.lastUpdated.toMillis()
              : undefined,
          };
        });

      callback(threads);
    },
    (err: any) => {
      // Ako dobijemo "prazan" error ili nešto što nije pravi Firestore error
      // ili nema code field, samo vratimo prazan array bez logovanja u konzolu.
      if (!err || typeof err !== "object") {
        callback([]);
        return;
      }

      // Provjeri da li je error objekat prazan ili nema korisne informacije
      const hasCode = typeof (err as any).code === "string" && (err as any).code.length > 0;
      const hasMessage = typeof (err as any).message === "string" && (err as any).message.length > 0;
      
      // Check if error object has any enumerable properties
      const errorKeys = Object.keys(err);
      const hasEnumerableProps = errorKeys.length > 0;
      
      // Check if error object is actually empty (no code, no message, no enumerable props)
      const isEmpty = !hasCode && !hasMessage && !hasEnumerableProps;
      
      if (isEmpty) {
        // Empty error object - just return without logging
        callback([]);
        return;
      }

      // Samo značajne Firestore greške logujemo (ignorišemo failed-precondition / unavailable)
      if (hasCode && err.code !== "failed-precondition" && err.code !== "unavailable") {
        // Build error log object with only defined properties
        const errorLog: any = {
          action: "subscribeToChatThreads",
          userId,
          path: "chats",
        };
        
        if (hasCode) {
          errorLog.code = err.code;
        }
        if (hasMessage) {
          errorLog.message = err.message;
        }
        
        // Include error object properties if available
        if (hasEnumerableProps && errorKeys.length > 0) {
          // Try to extract useful properties from error object
          const errorProps: any = {};
          errorKeys.forEach(key => {
            const value = err[key];
            if (value !== undefined && value !== null && typeof value !== "function") {
              // Only include serializable values
              try {
                JSON.stringify(value);
                errorProps[key] = value;
              } catch {
                // Skip non-serializable values
              }
            }
          });
          if (Object.keys(errorProps).length > 0) {
            errorLog.errorDetails = errorProps;
          }
        }
        
        // Only log if we have meaningful information – as warning to avoid noisy dev errors
        if (hasCode || hasMessage) {
          console.warn("[FIRESTORE] Error subscribing to chat threads:", errorLog);
        }
      }
      callback([]);
    }
    );

    return unsubscribe;
  } catch (error: any) {
    // Ako query ne može biti kreiran (npr. nedostaje index), samo vratimo prazan unsubscribe
    console.warn("[FIRESTORE] Failed to create chat threads subscription:", error?.message || error);
    callback([]);
    return () => {};
  }
}

// Mark all messages in a thread as read and set unread count to 0
export async function markThreadAsRead(threadKey: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId || !threadKey) {
    return;
  }

  try {
    // Update unread count in chat document to 0
    const chatRef = doc(db, "chats", threadKey);
    
    // Check if chat exists first
    const chatSnap = await getDoc(chatRef);
    if (!chatSnap.exists()) {
      return; // Chat doesn't exist, silently return
    }
    
    await updateDoc(chatRef, {
      [`unread.${userId}`]: 0,
    });

    // Also mark individual messages as read (for backward compatibility)
    const messagesRef = collection(db, "chats", threadKey, "messages");
    
    // Get all unread messages from others (not from current user)
    let snapshot;
    try {
      const q = query(
        messagesRef,
        where("from", "!=", userId),
        where("read", "==", false)
      );
      snapshot = await getDocs(q);
    } catch (queryError: any) {
      // If query fails (maybe index missing), get all messages and filter client-side
      const allMessagesQ = query(messagesRef, where("from", "!=", userId));
      const allSnapshot = await getDocs(allMessagesQ);
      snapshot = {
        docs: allSnapshot.docs.filter((doc) => {
          const data = doc.data();
          return data.read !== true && data.read !== "true" && String(data.read) !== "true";
        }),
      } as typeof allSnapshot;
    }
    
    if (snapshot.docs.length > 0) {
      // Update each message individually
      const updatePromises = snapshot.docs.map(async (doc) => {
        try {
          const data = doc.data();
          if (String(data.from) === userId) {
            return;
          }
          if (data.read === true || data.read === "true") {
            return;
          }
          await updateDoc(doc.ref, { read: true });
        } catch (updateError: any) {
          // Silently ignore all errors (permission-denied, not-found, etc. are expected)
          // No logging needed
        }
      });
      await Promise.all(updatePromises);
    }
  } catch (error: any) {
    // Silently ignore all errors (permission-denied, not-found, unavailable are all expected)
    // No logging needed - function is called frequently and errors are normal
  }
}

// Get count of unread messages for a specific thread
export async function getThreadUnreadCount(threadKey: string, userId: string): Promise<number> {
  try {
    const messagesRef = collection(db, "chats", threadKey, "messages");
    const allMessagesQ = query(messagesRef, where("from", "!=", userId));
    const allSnapshot = await getDocs(allMessagesQ);
    // Count ONLY unread messages (read is not explicitly true)
    const unreadCount = allSnapshot.docs.filter((doc) => {
      const data = doc.data();
      const readValue = data.read;
      // Count as unread if read is not explicitly true (boolean or string)
      return readValue !== true && readValue !== "true" && String(readValue) !== "true";
    }).length;
    return unreadCount;
  } catch (error: any) {
    console.error("[FIRESTORE]", {
      action: "getThreadUnreadCount",
      userId,
      threadKey,
      path: `chats/${threadKey}/messages`,
      error: error,
      code: error?.code,
      message: error?.message,
    });
    return 0;
  }
}

// Get count of unread messages for a user (using unread count from chats doc)
export async function getUnreadMessageCount(userId: string): Promise<number> {
  try {
    const threads = await getChatThreads(userId);
    // Sum unread counts from threads (already loaded from chats doc)
    const totalUnread = threads.reduce((sum, thread) => {
      return sum + (thread.unread?.[userId] || 0);
    }, 0);
    return totalUnread;
  } catch (error: any) {
    console.error("[FIRESTORE]", {
      action: "getUnreadMessageCount",
      userId,
      path: "chats",
      error: error,
      code: error?.code,
      message: error?.message,
    });
    return 0;
  }
}

// Soft delete a chat thread for current user AND hide all of its messages for that user
export async function deleteChatThread(threadKey: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  try {
    const chatRef = doc(db, "chats", threadKey);

    // 1) Soft delete the thread for the current user so it disappears from the inbox
    await updateDoc(chatRef, {
      [`deletedFor.${userId}`]: true,
      [`unread.${userId}`]: 0,
    });

    // 2) Soft delete all messages in this thread for the current user so that
    // opening a chat with this user again starts with a clean history.
    const messagesRef = collection(db, "chats", threadKey, "messages");
    const messagesSnapshot = await getDocs(messagesRef);

    if (!messagesSnapshot.empty) {
      let batch = writeBatch(db);
      let batchCount = 0;
      const MAX_BATCH_SIZE = 500; // Firestore batch limit

      for (const messageDoc of messagesSnapshot.docs) {
        batch.update(messageDoc.ref, {
          [`deletedFor.${userId}`]: true,
        });

        batchCount++;
        if (batchCount >= MAX_BATCH_SIZE) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }

      if (batchCount > 0) {
        await batch.commit();
      }
    }
  } catch (error: any) {
    console.error("[FIRESTORE]", {
      action: "deleteChatThread",
      userId,
      threadKey,
      path: `chats/${threadKey}`,
      error: error,
      code: error?.code,
      message: error?.message,
    });
    throw error;
  }
}

// Delete message for me (soft delete - hide from current user)
export async function deleteMessageForMe(threadKey: string, messageId: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  try {
    const messageRef = doc(db, "chats", threadKey, "messages", messageId);
    await updateDoc(messageRef, {
      [`deletedFor.${userId}`]: true,
    });
  } catch (error: any) {
    console.error("[FIRESTORE]", {
      action: "deleteMessageForMe",
      userId,
      threadKey,
      messageId,
      path: `chats/${threadKey}/messages/${messageId}`,
      error: error,
      code: error?.code,
      message: error?.message,
    });
    throw error;
  }
}

// Delete message for everyone (only sender can do this)
export async function deleteMessageForEveryone(threadKey: string, messageId: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  try {
    const messageRef = doc(db, "chats", threadKey, "messages", messageId);
    const messageSnap = await getDoc(messageRef);
    
    if (!messageSnap.exists()) {
      throw new Error("Message not found");
    }

    const messageData = messageSnap.data();
    if (messageData.from !== userId) {
      throw new Error("Only sender can delete message for everyone");
    }

    await updateDoc(messageRef, {
      text: "This message was deleted",
      deletedForEveryone: true,
    });
  } catch (error: any) {
    console.error("[FIRESTORE]", {
      action: "deleteMessageForEveryone",
      userId,
      threadKey,
      messageId,
      path: `chats/${threadKey}/messages/${messageId}`,
      error: error,
      code: error?.code,
      message: error?.message,
    });
    throw error;
  }
}

// Subscribe to unread message count
export function subscribeToUnreadCount(
  userId: string,
  callback: (count: number) => void
): () => void {
  if (!userId) {
    callback(0);
    return () => {};
  }

  let unsubscribeFunctions: Array<() => void> = [];
  const threadCounts = new Map<string, number>();
  let threadsUnsubscribe: (() => void) | null = null;

  const updateTotal = () => {
    let total = 0;
    threadCounts.forEach((count) => {
      total += count;
    });
    callback(total);
  };

  // Initial count
  getUnreadMessageCount(userId)
    .then((initialCount) => {
      callback(initialCount);
    })
    .catch((error) => {
      console.warn("Error getting initial unread count:", error);
      callback(0);
    });

  threadsUnsubscribe = subscribeToChatThreads(userId, (threads) => {
    // Use unread count from threads (already loaded from chats doc)
    threadCounts.clear();
    threads.forEach((thread) => {
      threadCounts.set(thread.threadKey, thread.unread?.[userId] || 0);
    });
    updateTotal();
  });

  return () => {
    if (threadsUnsubscribe) {
      threadsUnsubscribe();
    }
    unsubscribeFunctions.forEach((unsub) => unsub());
    unsubscribeFunctions = [];
    threadCounts.clear();
  };
}

// ---------- FCM TOKEN MANAGEMENT ----------
// Request notification permission and get FCM token
export async function requestNotificationPermission(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  
  try {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  } catch (error) {
    console.error("Error requesting notification permission:", error);
    return false;
  }
}

// Get FCM token and save it to Firestore
export async function getAndSaveFCMToken(): Promise<string | null> {
  const userId = getCurrentUserId();
  if (!userId) {
    console.warn("User not authenticated, cannot save FCM token");
    return null;
  }

  if (typeof window === "undefined") return null;

  try {
    // Check if notifications are supported
    if (!("Notification" in window)) {
      console.warn("Notifications not supported in this browser");
      return null;
    }

    // Request permission
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.warn("Notification permission not granted");
      return null;
    }

    // Get messaging instance (now async because it registers service worker)
    const { getMessagingInstance } = await import("./firebase");
    const messaging = await getMessagingInstance();
    if (!messaging) {
      console.warn("Firebase Messaging not available");
      return null;
    }

    // Get FCM token
    const { getToken } = await import("firebase/messaging");
    const vapidKey = process.env.NEXT_PUBLIC_FCM_VAPID_KEY || ""; // You'll need to set this in .env
    if (!vapidKey) {
      console.warn("FCM VAPID key not configured. Set NEXT_PUBLIC_FCM_VAPID_KEY in .env");
      return null;
    }

    const token = await getToken(messaging, { vapidKey });
    if (!token) {
      console.warn("No FCM token available");
      return null;
    }

    // Save token to Firestore
    const tokenRef = doc(db, "fcmTokens", userId);
    await setDoc(tokenRef, {
      token,
      updatedAt: Timestamp.now(),
    }, { merge: true });

    console.log("FCM token saved successfully");
    return token;
  } catch (error: any) {
    console.error("[FIRESTORE] Error getting/saving FCM token:", error);
    return null;
  }
}

// Delete FCM token (when user logs out or disables notifications)
export async function deleteFCMToken(): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) return;

  try {
    const tokenRef = doc(db, "fcmTokens", userId);
    await deleteDoc(tokenRef);
    console.log("FCM token deleted");
  } catch (error: any) {
    console.error("[FIRESTORE] Error deleting FCM token:", error);
  }
}

// ---------- NOTIFICATION SETTINGS ----------
// Update notification settings in user profile
export async function updateNotificationSettings(settings: {
  enabled?: boolean;
  muteChats?: string[];
  disableAll?: boolean;
}): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  const profileRef = doc(db, "profiles", userId);
  const current = await getProfile(userId);
  
  const updatedSettings = {
    ...(current.notificationSettings || {}),
    ...settings,
  };

  await updateDoc(profileRef, {
    notificationSettings: updatedSettings,
  });
}

// Mute a specific chat thread
export async function muteChatThread(threadKey: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  const profileRef = doc(db, "profiles", userId);
  const current = await getProfile(userId);
  const mutedChats = current.notificationSettings?.muteChats || [];
  
  if (!mutedChats.includes(threadKey)) {
    await updateDoc(profileRef, {
      "notificationSettings.muteChats": [...mutedChats, threadKey],
    });
  }
}

// Unmute a specific chat thread
export async function unmuteChatThread(threadKey: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  const profileRef = doc(db, "profiles", userId);
  const current = await getProfile(userId);
  const mutedChats = current.notificationSettings?.muteChats || [];
  
  if (mutedChats.includes(threadKey)) {
    await updateDoc(profileRef, {
      "notificationSettings.muteChats": mutedChats.filter((key) => key !== threadKey),
    });
  }
}

// Check if a chat thread is muted
export async function isChatMuted(threadKey: string): Promise<boolean> {
  const userId = getCurrentUserId();
  if (!userId) return false;

  try {
    const profile = await getProfile(userId);
    const mutedChats = profile.notificationSettings?.muteChats || [];
    return mutedChats.includes(threadKey);
  } catch (error) {
    console.error("Error checking if chat is muted:", error);
    return false;
  }
}

// Check if notifications are enabled for user
export async function areNotificationsEnabled(): Promise<boolean> {
  const userId = getCurrentUserId();
  if (!userId) return false;

  try {
    const profile = await getProfile(userId);
    const settings = profile.notificationSettings;
    
    // If disableAll is true, notifications are disabled
    if (settings?.disableAll) return false;
    
    // If enabled is explicitly false, notifications are disabled
    if (settings?.enabled === false) return false;
    
    // Default to enabled if not explicitly disabled
    return true;
  } catch (error) {
    console.error("Error checking notification settings:", error);
    return true; // Default to enabled on error
  }
}

// ---------- CHAT IMAGE UPLOAD ----------
// Upload image to Firebase Storage for chat messages
// Compress image before upload for faster sending
async function compressImage(file: File, maxWidth: number = 1920, maxHeight: number = 1920, quality: number = 0.85): Promise<File> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = width * ratio;
          height = height * ratio;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export async function uploadChatImage(file: File, threadKey: string, userId: string): Promise<string> {
  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error("Dozvoljeni su samo slikovni fajlovi.");
    }
    
    // Validate file size (max 5MB for images before compression)
    if (file.size > 5 * 1024 * 1024) {
      throw new Error("Slika ne može biti veća od 5MB.");
    }
    
    console.log(`[uploadChatImage] Compressing and converting to base64...`);
    console.log(`[uploadChatImage] File: ${file.name}, Size: ${file.size} bytes, Type: ${file.type}`);
    
    // Compress image for chat (max 1200x1200, 0.75 quality - good balance for chat images)
    // This keeps images reasonable for Firestore while maintaining good quality for chat
    const compressedFile = await compressImageForFirestore(file, 1200, 1200, 0.75);
    
    // Convert to base64 data URL - this avoids Firebase Storage completely
    // and stores the image directly in Firestore as a string
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Url = String(reader.result);
        console.log("[uploadChatImage] Base64 conversion completed, size:", base64Url.length, "chars");
        
        // Check if base64 string is too large for Firestore (1MB limit per field)
        // Base64 is ~33% larger than binary, so ~750KB binary = ~1MB base64
        if (base64Url.length > 1000000) {
          console.warn("[uploadChatImage] Base64 string is large, consider further compression");
        }
        
        resolve(base64Url);
      };
      reader.onerror = (error) => {
        console.error("[uploadChatImage] Error converting to base64:", error);
        reject(new Error("Greška pri konvertovanju slike u base64."));
      };
      reader.readAsDataURL(compressedFile);
    });
  } catch (error: any) {
    console.error("Error uploading chat image:", error);
    console.error("Error details:", {
      message: error?.message,
      code: error?.code,
      stack: error?.stack,
      name: error?.name
    });
    throw new Error(error?.message || "Greška pri učitavanju slike. Pokušaj ponovo.");
  }
}

// ---------- LISTING VIDEO UPLOAD ----------
// Helper function to convert base64 to File
function base64ToFile(base64: string, filename: string): File {
  const arr = base64.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

// Migrate base64 image to Firebase Storage
async function migrateBase64Image(base64: string, listingId: string, userId: string, index: number): Promise<string> {
  try {
    const { getStorage, ref, uploadBytes, getDownloadURL } = await import("firebase/storage");
    const storage = getStorage();
    
    // Convert base64 to File
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 9);
    const ext = base64.includes('image/png') ? 'png' : base64.includes('image/webp') ? 'webp' : 'jpg';
    const fileName = `listing_${listingId}_migrated_${timestamp}_${index}_${random}.${ext}`;
    const file = base64ToFile(base64, fileName);
    
    // Check file size
    if (file.size > 5 * 1024 * 1024) {
      console.warn(`Image ${index} is too large (${file.size} bytes), skipping migration`);
      return base64; // Return original if too large
    }
    
    const storagePath = `listing-images/${userId}/${fileName}`;
    const imageRef = ref(storage, storagePath);
    
    // Upload to Storage
    await uploadBytes(imageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(imageRef);
    
    return downloadURL;
  } catch (error) {
    console.error(`Error migrating base64 image ${index}:`, error);
    return base64; // Return original on error
  }
}

// Check if string is base64 image
function isBase64Image(str: string): boolean {
  return typeof str === 'string' && str.startsWith('data:image/');
}

// Upload image to Firebase Storage for listings
export async function uploadListingImage(file: File, listingId: string, userId: string): Promise<string> {
  try {
    const { ref, uploadBytes, getDownloadURL } = await import("firebase/storage");
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error("Dozvoljeni su samo slikovni fajlovi.");
    }
    
    // Validate file size (max 5MB for images)
    if (file.size > 5 * 1024 * 1024) {
      throw new Error("Slika ne može biti veća od 5MB.");
    }
    
    // Create unique filename: listing_{listingId}_{timestamp}_{random}.{ext}
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 9);
    const ext = file.name.split('.').pop() || 'jpg';
    const fileName = `listing_${listingId || 'new'}_${timestamp}_${random}.${ext}`;
    const storagePath = `listing-images/${userId}/${fileName}`;
    
    console.log(`[uploadListingImage] Uploading to: ${storagePath}`);
    console.log(`[uploadListingImage] File: ${file.name}, Size: ${file.size} bytes, Type: ${file.type}`);
    
    if (!storage) {
      throw new Error("Firebase Storage nije inicijaliziran");
    }
    
    const imageRef = ref(storage, storagePath);
    
    // Upload image file
    console.log(`[uploadListingImage] Starting uploadBytes...`);
    const uploadResult = await uploadBytes(imageRef, file);
    console.log(`[uploadListingImage] Upload completed, metadata:`, uploadResult.metadata);
    
    // Get download URL
    console.log(`[uploadListingImage] Getting download URL...`);
    const downloadURL = await getDownloadURL(imageRef);
    console.log(`[uploadListingImage] Download URL obtained: ${downloadURL}`);
    
    return downloadURL;
  } catch (error: any) {
    console.error("[uploadListingImage] Error uploading listing image:", error);
    console.error("[uploadListingImage] Error details:", {
      message: error?.message,
      code: error?.code,
      stack: error?.stack,
      name: error?.name
    });
    throw new Error(error?.message || "Greška pri učitavanju slike. Pokušaj ponovo.");
  }
}

// Upload avatar image - using base64 to avoid CORS issues (same as listing images)
// NOTE: Using base64 directly since Storage Rules are not configured, this avoids CORS issues completely
export async function uploadAvatarImage(file: File, userId: string): Promise<string> {
  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error("Dozvoljeni su samo slikovni fajlovi.");
    }
    
    // Validate file size (max 10MB before compression)
    if (file.size > 10 * 1024 * 1024) {
      throw new Error("Slika ne može biti veća od 10MB.");
    }
    
    console.log(`[uploadAvatarImage] Compressing and converting to base64...`);
    console.log(`[uploadAvatarImage] File: ${file.name}, Size: ${file.size} bytes, Type: ${file.type}`);
    
    // Compress image for avatar (max 800x800, quality 0.8 - good quality for profile pictures)
    const compressedFile = await compressImageForFirestore(file, 800, 800, 0.8);
    console.log(`[uploadAvatarImage] Compressed to: ${compressedFile.size} bytes`);
    
    // Use base64 directly since Storage Rules are not configured
    // This avoids CORS issues completely
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Url = String(reader.result);
        console.log("[uploadAvatarImage] Base64 conversion completed, size:", base64Url.length, "chars");
        resolve(base64Url);
      };
      reader.onerror = (error) => {
        console.error("[uploadAvatarImage] Error converting to base64:", error);
        reject(new Error("Greška pri konvertovanju slike u base64."));
      };
      reader.readAsDataURL(compressedFile);
    });
  } catch (error: any) {
    console.error("[uploadAvatarImage] Error uploading avatar image:", error);
    console.error("[uploadAvatarImage] Error details:", {
      message: error?.message,
      code: error?.code,
      stack: error?.stack,
      name: error?.name
    });
    throw new Error(error?.message || "Greška pri učitavanju avatar slike. Pokušaj ponovo.");
  }
}

// Compress image for Firestore (max 600x600, quality 0.6 to keep size under 1MB limit for multiple images)
async function compressImageForFirestore(file: File, maxWidth: number = 600, maxHeight: number = 600, quality: number = 0.6): Promise<File> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = width * ratio;
          height = height * ratio;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            console.log(`[compressImageForFirestore] Compressed from ${file.size} bytes to ${compressedFile.size} bytes`);
            resolve(compressedFile);
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Upload image to Firebase Storage for listings with progress callback
// NOTE: Currently using base64 fallback due to CORS issues without Storage Rules
export async function uploadListingImageWithProgress(
  file: File, 
  listingId: string, 
  userId: string,
  onProgress?: (progress: number) => void
): Promise<string> {
  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error("Dozvoljeni su samo slikovni fajlovi.");
    }
    
    // Validate file size (max 5MB for images)
    if (file.size > 5 * 1024 * 1024) {
      throw new Error("Slika ne može biti veća od 5MB.");
    }
    
    console.log(`[uploadListingImageWithProgress] Compressing and converting to base64...`);
    console.log(`[uploadListingImageWithProgress] File: ${file.name}, Size: ${file.size} bytes, Type: ${file.type}`);
    
    // Compress image first to reduce size for Firestore (1MB limit per document)
    if (onProgress) onProgress(20);
    const compressedFile = await compressImageForFirestore(file, 800, 800, 0.7);
    
    if (onProgress) onProgress(60);
    
    // Use base64 directly since Storage Rules are not configured
    // This avoids CORS issues completely
    return new Promise((resolve, reject) => {
      if (onProgress) onProgress(80);
      
      const reader = new FileReader();
      reader.onload = () => {
        const base64Url = String(reader.result);
        if (onProgress) {
          onProgress(100);
        }
        console.log("[uploadListingImageWithProgress] Base64 conversion completed, size:", base64Url.length, "chars");
        resolve(base64Url);
      };
      reader.onerror = (error) => {
        console.error("[uploadListingImageWithProgress] Error converting to base64:", error);
        reject(new Error("Greška pri konvertovanju slike u base64."));
      };
      reader.readAsDataURL(compressedFile);
    });
  } catch (error: any) {
    console.error("[uploadListingImageWithProgress] Error uploading listing image:", error);
    console.error("[uploadListingImageWithProgress] Error details:", {
      message: error?.message,
      code: error?.code,
      stack: error?.stack,
      name: error?.name
    });
    throw new Error(error?.message || "Greška pri učitavanju slike. Pokušaj ponovo.");
  }
}

// Upload video to Firebase Storage for listings
export async function uploadListingVideo(file: File, listingId: string, userId: string): Promise<string> {
  try {
    const { getStorage, ref, uploadBytes, getDownloadURL } = await import("firebase/storage");
    const storage = getStorage();
    
    // Validate file type
    if (!file.type.startsWith('video/')) {
      throw new Error("Dozvoljeni su samo video fajlovi.");
    }
    
    // Validate file size (max 50MB for videos)
    if (file.size > 50 * 1024 * 1024) {
      throw new Error("Video ne može biti veći od 50MB.");
    }
    
    // Create unique filename: listing_{listingId}_{timestamp}_{random}.{ext}
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 9);
    const ext = file.name.split('.').pop() || 'mp4';
    const fileName = `listing_${listingId || 'new'}_${timestamp}_${random}.${ext}`;
    const storagePath = `listing-videos/${userId}/${fileName}`;
    
    const videoRef = ref(storage, storagePath);
    
    // Upload video file
    await uploadBytes(videoRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(videoRef);
    
    return downloadURL;
  } catch (error) {
    console.error("Error uploading listing video:", error);
    throw new Error("Greška pri učitavanju videa. Pokušaj ponovo.");
  }
}

// ---------- CONNECTIONS / FRIENDS SYSTEM ----------
// Similar to LinkedIn - bidirectional connections after acceptance

// Send a connection request to another user
export async function sendConnectionRequest(targetUserId: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");
  
  if (userId === targetUserId) {
    throw new Error("Ne možete se povezati sa sobom");
  }

  // Check if already connected or request already exists
  const status = await getConnectionStatus(targetUserId);
  if (status === "connected") {
    throw new Error("Već ste povezani sa ovim korisnikom");
  }
  if (status === "pending_sent") {
    throw new Error("Već ste poslali zahtjev za povezivanje");
  }
  if (status === "pending_received") {
    // Auto-accept if they sent us a request
    await acceptConnection(targetUserId);
    return;
  }

  // Create connection request document
  // Store in both users' connectionRequests subcollection for easy querying
  const requestId = `${userId}_${targetUserId}`;
  
  // First, check if request already exists in sender's collection
  const senderRequestRef = doc(db, "profiles", userId, "connectionRequests", requestId);
  const senderRequestSnap = await getDoc(senderRequestRef);
  
  if (senderRequestSnap.exists()) {
    // Request already exists, don't throw error - just return silently
    return;
  }
  
  // Store in sender's outgoing requests first
  try {
    await setDoc(senderRequestRef, {
      fromUserId: userId,
      toUserId: targetUserId,
      status: "pending",
      createdAt: numberToTimestamp(Date.now()),
    });
  } catch (error: any) {
    // If we can't write to sender's collection, that's a real error
    console.error("Error creating connection request in sender's collection:", error);
    // Check if it's a permission error
    if (error?.code === "permission-denied" || error?.message?.includes("permission")) {
      throw new Error("Nemate dozvolu za slanje zahtjeva. Provjerite Firebase security rules.");
    }
    throw new Error(error?.message || "Greška pri slanju zahtjeva za povezivanje");
  }

  // Try to store in receiver's incoming requests
  // If this fails due to permissions, that's okay - the request is still stored in sender's collection
  const receiverRequestRef = doc(db, "profiles", targetUserId, "connectionRequests", requestId);
  try {
    await setDoc(receiverRequestRef, {
      fromUserId: userId,
      toUserId: targetUserId,
      status: "pending",
      createdAt: numberToTimestamp(Date.now()),
    });
  } catch (error: any) {
    // If we can't write to receiver's collection due to permissions, that's okay
    // The request is still stored in sender's collection and will be visible to sender
    // The receiver will see it when they check their requests (if they have read permissions)
    if (error?.code === "permission-denied" || error?.message?.includes("permission")) {
      // Silently ignore permission errors - request is still saved in sender's collection
      // This is expected behavior due to Firestore security rules
      return;
    }
    // For other errors, re-throw
    throw error;
  }
}

// Accept a connection request
export async function acceptConnection(targetUserId: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Find the request (could be from either direction)
  // Request might be in current user's collection OR in sender's collection (due to permissions)
  const requestId1 = `${userId}_${targetUserId}`;
  const requestId2 = `${targetUserId}_${userId}`;
  
  // First, check in current user's collection (receiver's collection)
  let requestRef = doc(db, "profiles", userId, "connectionRequests", requestId2);
  let requestSnap = await getDoc(requestRef);
  
  if (!requestSnap.exists()) {
    requestRef = doc(db, "profiles", userId, "connectionRequests", requestId1);
    requestSnap = await getDoc(requestRef);
  }

  // If not found in current user's collection, check in sender's collection
  // This handles cases where request was only saved in sender's collection due to permissions
  if (!requestSnap.exists() || requestSnap.data()?.status !== "pending") {
    // Try to find in sender's collection
    const senderRequestRef1 = doc(db, "profiles", targetUserId, "connectionRequests", requestId1);
    const senderRequestRef2 = doc(db, "profiles", targetUserId, "connectionRequests", requestId2);
    
    let senderRequestSnap = await getDoc(senderRequestRef1);
    if (!senderRequestSnap.exists() || senderRequestSnap.data()?.status !== "pending") {
      senderRequestSnap = await getDoc(senderRequestRef2);
    }
    
    if (senderRequestSnap.exists() && senderRequestSnap.data()?.status === "pending") {
      // Found in sender's collection, use that
      requestRef = senderRequestSnap.ref;
      requestSnap = senderRequestSnap;
    } else {
      // Still not found, throw error
      throw new Error("Zahtjev za povezivanje nije pronađen");
    }
  }

  const requestData = requestSnap.data();
  if (!requestData) {
    throw new Error("Zahtjev za povezivanje nije pronađen");
  }
  const fromUserId = requestData.fromUserId;
  const toUserId = requestData.toUserId;

  // Update both request documents to accepted
  const now = numberToTimestamp(Date.now());
  
  // Update in current user's requests
  await updateDoc(requestRef, {
    status: "accepted",
    acceptedAt: now,
  });

  // Update in other user's requests (might fail due to permissions, that's okay)
  const otherRequestRef = doc(db, "profiles", fromUserId === userId ? toUserId : fromUserId, "connectionRequests", requestSnap.id);
  try {
    await updateDoc(otherRequestRef, {
      status: "accepted",
      acceptedAt: now,
    });
  } catch (error: any) {
    // If we can't update in other user's collection due to permissions, that's okay
    // The request is already updated in current user's collection
    if (error?.code !== "permission-denied" && !error?.message?.includes("permission")) {
      throw error; // Re-throw if it's not a permission error
    }
    // Silently ignore permission errors
  }

  // Create bidirectional connection documents
  const connectionId = [userId, targetUserId].sort().join("_");
  
  // Store in current user's connections
  const myConnectionRef = doc(db, "profiles", userId, "connections", targetUserId);
  await setDoc(myConnectionRef, {
    userId: targetUserId,
    connectedAt: now,
  });

  // Store in other user's connections (might fail due to permissions, that's okay)
  const theirConnectionRef = doc(db, "profiles", targetUserId, "connections", userId);
  try {
    await setDoc(theirConnectionRef, {
      userId: userId,
      connectedAt: now,
    });
  } catch (error: any) {
    // If we can't create in other user's collection due to permissions, that's okay
    // The connection is already created in current user's collection
    if (error?.code !== "permission-denied" && !error?.message?.includes("permission")) {
      throw error; // Re-throw if it's not a permission error
    }
    // Silently ignore permission errors - connection is created in current user's collection
  }
}

// Reject a connection request
export async function rejectConnection(targetUserId: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  const requestId1 = `${userId}_${targetUserId}`;
  const requestId2 = `${targetUserId}_${userId}`;
  
  // Check both possible request IDs
  let requestRef = doc(db, "profiles", userId, "connectionRequests", requestId2);
  let requestSnap = await getDoc(requestRef);
  
  if (!requestSnap.exists()) {
    requestRef = doc(db, "profiles", userId, "connectionRequests", requestId1);
    requestSnap = await getDoc(requestRef);
  }

  if (!requestSnap.exists()) {
    // Request might have already been deleted, just return silently
    return;
  }

  // Delete request from current user's collection first
  try {
    await deleteDoc(requestRef);
  } catch (error: any) {
    // If deletion fails, check if document still exists
    const checkSnap = await getDoc(requestRef);
    if (checkSnap.exists()) {
      throw error; // Re-throw if document still exists
    }
    // Document was already deleted, continue silently
  }
  
  // Try to delete from other user's collection (might fail due to permissions, that's okay)
  const otherUserId = requestSnap.data()?.fromUserId === userId 
    ? requestSnap.data()?.toUserId 
    : requestSnap.data()?.fromUserId;
  
  if (otherUserId) {
    const otherRequestRef = doc(db, "profiles", otherUserId, "connectionRequests", requestSnap.id);
    try {
      await deleteDoc(otherRequestRef);
    } catch (error: any) {
      // If we can't delete from other user's collection due to permissions, that's okay
      // The request is already deleted from current user's collection
      if (error?.code === "permission-denied" || error?.message?.includes("permission")) {
        // Silently ignore permission errors - request is deleted from current user's side
        // This is expected behavior due to Firestore security rules
        return; // Success - request is deleted from current user's side
      }
      // For other errors, silently ignore (request is already deleted from current user)
    }
  }
}

// Remove a connection (unfriend)
export async function removeConnection(targetUserId: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("User must be authenticated");

  // Delete connection from both users
  const myConnectionRef = doc(db, "profiles", userId, "connections", targetUserId);
  await deleteDoc(myConnectionRef);

  const theirConnectionRef = doc(db, "profiles", targetUserId, "connections", userId);
  await deleteDoc(theirConnectionRef);

  // Also delete any connection requests
  const requestId1 = `${userId}_${targetUserId}`;
  const requestId2 = `${targetUserId}_${userId}`;
  
  const requestRef1 = doc(db, "profiles", userId, "connectionRequests", requestId1);
  const requestRef2 = doc(db, "profiles", userId, "connectionRequests", requestId2);
  
  try {
    await deleteDoc(requestRef1);
  } catch (e) {
    // Ignore if doesn't exist
  }
  
  try {
    await deleteDoc(requestRef2);
  } catch (e) {
    // Ignore if doesn't exist
  }

  // Delete from other user's requests too
  // Note: This requires the delete rule to allow deletion if user is participant in the request
  try {
    const otherRequestRef1 = doc(db, "profiles", targetUserId, "connectionRequests", requestId1);
    const otherRequestSnap1 = await getDoc(otherRequestRef1);
    if (otherRequestSnap1.exists()) {
      await deleteDoc(otherRequestRef1);
    }
  } catch (e: any) {
    // Ignore if doesn't exist or permission denied (might not have permission to delete from other user's subcollection)
    if (e?.code !== "not-found" && e?.code !== "permission-denied") {
      console.warn("Error deleting connection request from other user (1):", e);
    }
  }
  
  try {
    const otherRequestRef2 = doc(db, "profiles", targetUserId, "connectionRequests", requestId2);
    const otherRequestSnap2 = await getDoc(otherRequestRef2);
    if (otherRequestSnap2.exists()) {
      await deleteDoc(otherRequestRef2);
    }
  } catch (e: any) {
    // Ignore if doesn't exist or permission denied
    if (e?.code !== "not-found" && e?.code !== "permission-denied") {
      console.warn("Error deleting connection request from other user (2):", e);
    }
  }
}

// Get connection status between current user and target user
export async function getConnectionStatus(targetUserId: string): Promise<ConnectionStatus> {
  const userId = getCurrentUserId();
  if (!userId || userId === targetUserId) return "none";

  try {
    // Check if already connected
    const connectionRef = doc(db, "profiles", userId, "connections", targetUserId);
    const connectionSnap = await getDoc(connectionRef);
    if (connectionSnap.exists()) {
      return "connected";
    }

    // Check for pending requests
    const requestId1 = `${userId}_${targetUserId}`;
    const requestId2 = `${targetUserId}_${userId}`;
    
    const requestRef1 = doc(db, "profiles", userId, "connectionRequests", requestId1);
    const requestRef2 = doc(db, "profiles", userId, "connectionRequests", requestId2);
    
    try {
      const requestSnap1 = await getDoc(requestRef1);
      if (requestSnap1.exists()) {
        const data = requestSnap1.data();
        if (data?.status === "pending") {
          return data.fromUserId === userId ? "pending_sent" : "pending_received";
        }
      }
    } catch (error) {
      // Ignore permission errors for non-existent documents
      console.warn("Error reading connection request 1:", error);
    }
    
    try {
      const requestSnap2 = await getDoc(requestRef2);
      if (requestSnap2.exists()) {
        const data = requestSnap2.data();
        if (data?.status === "pending") {
          return data.fromUserId === userId ? "pending_sent" : "pending_received";
        }
      }
    } catch (error) {
      // Ignore permission errors for non-existent documents
      console.warn("Error reading connection request 2:", error);
    }

    return "none";
  } catch (error: any) {
    // If it's a permission error, user might not be fully authenticated yet
    if (error?.code === "permission-denied" || error?.code === "missing-or-insufficient-permissions") {
      console.warn("Permission denied getting connection status - user might not be authenticated yet");
      return "none";
    }
    console.error("Error getting connection status:", error);
    return "none";
  }
}

// Get all connections (friends) for current user
export async function getConnections(): Promise<Array<{ userId: string; connectedAt: number }>> {
  const userId = getCurrentUserId();
  if (!userId) return [];

  try {
    const connectionsRef = collection(db, "profiles", userId, "connections");
    const snapshot = await getDocs(connectionsRef);
    return snapshot.docs.map((doc) => ({
      userId: doc.id,
      connectedAt: timestampToNumber(doc.data().connectedAt),
    }));
  } catch (error) {
    console.error("Error fetching connections:", error);
    return [];
  }
}

// Get pending connection requests (both sent and received)
export async function getPendingRequests(): Promise<{
  sent: Array<{ userId: string; createdAt: number }>;
  received: Array<{ userId: string; createdAt: number }>;
}> {
  const userId = getCurrentUserId();
  if (!userId) return { sent: [], received: [] };

  try {
    // Get sent requests from current user's collection
    const requestsRef = collection(db, "profiles", userId, "connectionRequests");
    const snapshot = await getDocs(requestsRef);
    
    const sent: Array<{ userId: string; createdAt: number }> = [];
    const received: Array<{ userId: string; createdAt: number }> = [];

    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      
      // Check if status is pending or undefined (for backward compatibility)
      if (data.status === "pending" || !data.status) {
        if (data.fromUserId === userId) {
          sent.push({
            userId: data.toUserId,
            createdAt: timestampToNumber(data.createdAt),
          });
        } else if (data.toUserId === userId) {
          received.push({
            userId: data.fromUserId,
            createdAt: timestampToNumber(data.createdAt),
          });
        }
      }
    });

    // Note: Due to Firestore security rules, we can't read other users' connectionRequests subcollections
    // Received requests will only appear if they were successfully saved to current user's collection
    // To fix this properly, you need to update Firestore security rules to allow reading connectionRequests
    // Example rule:
    // match /profiles/{profileId}/connectionRequests/{requestId} {
    //   allow read: if request.auth != null && (
    //     resource.data.toUserId == request.auth.uid || 
    //     resource.data.fromUserId == request.auth.uid
    //   );
    // }

    return { sent, received };
  } catch (error) {
    console.error("Error fetching pending requests:", error);
    return { sent: [], received: [] };
  }
}